(() => {
  'use strict';

  const CATEGORY_IDS = Object.freeze(['viso', 'capelli', 'corpo', 'makeup']);
  const PUBLIC_WORLDS = Object.freeze([...CATEGORY_IDS, 'epilogue']);
  const VIDEO_DURATION = 44.086003;
  const FRAME_DURATION = 1 / 24;
  const CHAPTER_PLAYBACK_RATE = 1;
  const CHAPTER_TIME_SCALE = 1 / CHAPTER_PLAYBACK_RATE;
  const HERO_STILL_TIME = 2.35;
  const CONFIG_TOLERANCE = 0.001;
  const VIDEO_DURATION_TOLERANCE = 0.25;
  const SEEK_EPSILON = 0.001;
  const INTRO_STALL_TIMEOUT = 2500;
  const AUTOMATIC_SEEK_MIN_INTERVAL = 220;
  const AUTO_TIMINGS = Object.freeze({
    previousExit: 600,
    interPhraseBreathing: 400,
    closingBreathing: 1000,
    portalReveal: 3400,
    categoryReveal: 1800,
    epilogueReveal: 4200,
    previousReturn: 1200,
    phraseMinimum: 3600,
    phraseMaximum: 5800,
    phraseWordsPerSecond: 3,
    phraseReadingBuffer: 1200,
  });
  const AUTO_CANCEL_KEYS = new Set([
    'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp',
    'End', 'Escape', 'Home', 'PageDown', 'PageUp', ' ',
  ]);
  const JOURNEY_STATES = Object.freeze({
    introReady: 'INTRO_READY',
    playingViso: 'PLAYING_TO_VISO',
    viso: 'VISO',
    playingCapelli: 'PLAYING_TO_CAPELLI',
    capelli: 'CAPELLI',
    playingCorpo: 'PLAYING_TO_CORPO',
    corpo: 'CORPO',
    playingMakeup: 'PLAYING_TO_MAKEUP',
    makeup: 'MAKEUP',
  });
  const JOURNEY_STATE_VALUES = new Set(Object.values(JOURNEY_STATES));
  const TOUCH_INTENT_THRESHOLD = 12;
  const V2_PHASES = Object.freeze({
    previousExitEnd: 0.05,
    phrase1Start: 0.05,
    phrase1End: 0.17,
    phrase2Start: 0.18,
    phrase2End: 0.30,
    phrase3Start: 0.31,
    phrase3End: 0.44,
    phrase4Start: 0.45,
    phrase4End: 0.58,
    breathingEnd: 0.63,
    curtainCloseStart: 0.58,
    curtainClosed: 0.63,
    titleEnterStart: 0.63,
    titleEnterEnd: 0.70,
    titleExitStart: 0.70,
    titleExitEnd: 0.76,
    portalOpenStart: 0.74,
    portalOpenEnd: 0.86,
    categoryRevealStart: 0.74,
    categoryStable: 0.86,
    epilogueStable: 1,
  });
  const INTRO_TIMINGS = Object.freeze({
    blackEnd: 120,
    titleRevealEnd: 720,
    titleHoldEnd: 1000,
    titleToFilmEnd: 1380,
  });
  const INTRO_PRESET_TIMINGS = Object.freeze({
    compact: INTRO_TIMINGS,
    balanced: INTRO_TIMINGS,
    extended: INTRO_TIMINGS,
  });
  const INTRO_STATES = Object.freeze({
    black: 'intro-black',
    titleReveal: 'intro-title-reveal',
    titleToFilm: 'intro-title-to-film',
    autoplay: 'laura-autoplay',
    hold: 'intro-hold',
    scroll: 'scroll-film',
  });
  const PHRASE_MOTIONS = Object.freeze(['depth-reveal', 'masked-glide', 'near-field', 'bridge-center']);
  const DEFAULT_CONFIG = Object.freeze({
    intro: Object.freeze({ ctaTime: HERO_STILL_TIME, holdTime: 2.541667, blackPreset: 'balanced' }),
    categories: Object.freeze([
      Object.freeze({ id: 'viso', videoStart: 0, videoEnd: 10.916667, stableProgress: 0.86 }),
      Object.freeze({ id: 'capelli', videoStart: 10.916667, videoEnd: 21.833333, stableProgress: 0.86 }),
      Object.freeze({ id: 'corpo', videoStart: 21.833333, videoEnd: 32.958333, stableProgress: 0.86 }),
      Object.freeze({ id: 'makeup', videoStart: 32.958333, videoEnd: 43.833333, stableProgress: 0.86 }),
    ]),
    epilogue: Object.freeze({ videoStart: 43.833333, blackTarget: 44.0, videoEnd: 44.041667 }),
    desktopTravelVh: 130,
    mobileTravelVh: 145,
    titleEnterScale: 0.72,
    titleExitScale: 3.2,
    showSkip: true,
    motionIntensity: 'balanced',
  });
  const INSTANCES = new WeakMap();
  const CONNECTED = new Set();
  const ANALYTICS_FIELDS = new Set([
    'asset_type',
    'category',
    'certification_id',
    'config_status',
    'destination',
    'from_state',
    'method',
    'motion_intensity',
    'phrase_index',
    'reason',
    'reduced_motion',
    'result',
    'save_data',
    'source',
    'state',
    'tier',
    'world_id',
  ]);
  const STYLE_PROPERTIES = Object.freeze([
    '--ld-film-progress',
    '--ld-film-intro-progress',
    '--ld-film-hero-crossfade',
    '--ld-film-phrase-progress',
    '--ld-film-curtain-opacity',
    '--ld-film-title-opacity',
    '--ld-film-title-x',
    '--ld-film-title-clip',
    '--ld-film-return-clip',
    '--ld-film-image-opacity',
    '--ld-film-image-scale',
    '--ld-film-image-drift',
    '--ld-film-content-opacity',
    '--ld-film-copy-opacity',
    '--ld-film-cta-opacity',
    '--ld-film-certs-opacity',
    '--ld-v2-title-depth-scale',
    '--ld-v2-title-blur',
    '--ld-v2-portal-radius',
    '--ld-v2-portal-inset',
    '--ld-v2-portal-opacity',
    '--ld-v2-phrase-x',
    '--ld-v2-phrase-y',
    '--ld-v2-phrase-opacity',
    '--ld-v2-phrase-scale',
    '--ld-v2-phrase-blur',
    '--ld-v2-phrase-clip',
    '--ld-v2-category-reveal',
  ]);

  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
  const round = (value) => Math.round(value * 10000) / 10000;
  const segment = (value, start, end) => clamp((value - start) / Math.max(0.0001, end - start));
  const easeInOutCubic = (value) => {
    const progress = clamp(value);
    return progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
  };
  const easeOutCubic = (value) => 1 - Math.pow(1 - clamp(value), 3);
  const lerp = (start, end, progress) => start + (end - start) * clamp(progress);
  const scrollTargetAtOrAfter = (geometry, progress) => Math.ceil(geometry.start + geometry.length * clamp(progress));
  const finite = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeCategory = (value) => CATEGORY_IDS.includes(value) ? value : null;
  const originalAttribute = (element, name) => ({
    present: Boolean(element?.hasAttribute(name)),
    value: element?.getAttribute(name),
  });
  const restoreAttribute = (element, name, record) => {
    if (!element || !record) return;
    if (record.present) element.setAttribute(name, record.value ?? '');
    else element.removeAttribute(name);
  };
  const safeDestination = (value) => {
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin ? url.pathname : '';
    } catch (_error) {
      return '';
    }
  };
  const eventPayload = (name, detail) => {
    const payload = { event: name };
    Object.entries(detail || {}).forEach(([key, value]) => {
      if (!ANALYTICS_FIELDS.has(key)) return;
      if (typeof value === 'boolean' || typeof value === 'number') payload[key] = value;
      else if (typeof value === 'string') payload[key] = value.slice(0, 160);
    });
    return Object.freeze(payload);
  };

  class LDHomeFilmController {
    constructor(root, options = {}) {
      this.root = root;
      this.track = root.querySelector('[data-ld-home-track], .ld-home-film__track');
      this.stage = root.querySelector('[data-ld-home-stage], .ld-home-film__stage');
      this.video = root.querySelector('[data-ld-home-video]');
      this.identity = root.querySelector('[data-ld-identity], .ld-home-film__identity');
      this.introActions = root.querySelector('[data-ld-intro-actions]');
      this.scrollCue = root.querySelector('[data-ld-scroll-cue]');
      this.phraseLayer = root.querySelector('[data-ld-phrase-layer]');
      this.phrases = Array.from(root.querySelectorAll('[data-ld-phrase][data-category][data-index]'));
      this.curtain = root.querySelector('[data-ld-curtain], .ld-home-film__curtain');
      this.title = root.querySelector('[data-ld-title], [data-ld-giant-title], .ld-home-film__giant-title');
      this.signature = root.querySelector('[data-ld-signature], [data-ld-title-signature], .ld-home-film__signature');
      this.visualCategories = Array.from(root.querySelectorAll('[data-ld-visual-category][data-category]'));
      this.semanticFlow = root.querySelector('[data-ld-semantic-flow]');
      this.semanticCategories = Array.from(root.querySelectorAll('[data-ld-semantic-category][data-category], [data-ld-semantic-chapter][data-category]'));
      this.epilogue = root.querySelector('[data-ld-epilogue]');
      this.skip = root.querySelector('[data-ld-skip]');
      this.skipDestination = this.skip?.querySelector('[data-ld-skip-destination]') || null;
      this.skipLabels = this.skip
        ? Array.from(this.skip.querySelectorAll('.ld-luxury-action-v2__label, .visually-hidden'))
        : [];
      this.liveRegion = root.querySelector('[data-ld-live-region]');
      this.shortcuts = root.querySelector('[data-dv2-shortcuts]');
      this.headerGroup = document.getElementById('header-group');
      this.navigation = root.querySelector('[data-ld-home-nav-v2]');
      this.previousButton = this.navigation?.querySelector('[data-ld-chapter-previous]') || null;
      this.nextButton = this.navigation?.querySelector('[data-ld-chapter-next]') || null;
      this.navigationCurrent = this.navigation?.querySelector('[data-ld-nav-current]') || null;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.shortViewportQuery = window.matchMedia('(max-height: 679px), (orientation: landscape) and (max-width: 1023px)');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
      this.designMode = root.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      this.forcedStatic = Boolean(options.forcedStatic);
      this.configDiagnostics = [];
      this.config = this.readConfig();
      this.geometry = null;
      this.geometryDirty = true;
      this.frame = 0;
      this.unsubscribeCoordinator = null;
      this.inCoordinatorFrame = false;
      this.introFrame = 0;
      this.introSequenceFrame = 0;
      this.automaticFrame = 0;
      this.automaticPlayback = null;
      this.automaticMediaToken = 0;
      this.automaticSeekPending = null;
      this.automaticSeekInFlight = false;
      this.automaticSeekFrameCallback = 0;
      this.automaticSeekTimer = 0;
      this.automaticSeekLastAt = 0;
      this.automaticPresentedFrameCallback = 0;
      this.automaticPresentedMediaTime = null;
      this.manualVideoAnchor = null;
      this.journeyState = JOURNEY_STATES.introReady;
      this.transitionInProgress = false;
      this.journeyAnchorY = window.scrollY;
      this.journeyInternalScroll = false;
      this.journeyReleased = false;
      this.appPreviewAutoOpened = false;
      this.touchStartY = null;
      this.touchHandled = false;
      this.journeyLastMediaTime = null;
      this.journeyLastProgressAt = 0;
      this.previousScrollY = window.scrollY;
      this.direction = 'forward';
      this.activeWorld = 'handoff';
      this.activeState = 'boot';
      this.activeLocalProgress = 0;
      this.activeCategoryIndex = -1;
      this.lastSegmentIndex = -1;
      this.pendingStableAnnouncement = null;
      this.introConsumed = false;
      this.introPlaying = false;
      this.introControlsRevealed = false;
      this.introState = INTRO_STATES.black;
      this.introSequenceStarted = false;
      this.introSequenceCompleted = false;
      this.introSequenceRunning = false;
      this.introSequenceElapsed = 0;
      this.introSequenceStartedAt = 0;
      this.introLastVideoTime = 0;
      this.introLastProgressAt = 0;
      this.mediaReady = false;
      this.mediaFallback = false;
      this.activeSource = '';
      this.activeSourceKind = '';
      this.mediaSwapNormalized = null;
      this.resumeIntroAfterSwap = false;
      this.resumeIntroOnUnsuspend = false;
      this.targetVideoTime = this.config.intro.holdTime;
      this.suspensions = new Set();
      this.eventKeys = new Set();
      this.timers = new Set();
      this.tooltipTimers = new Map();
      this.mediaQueryCleanups = [];
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.dialogObserver = null;
      this.destroyed = false;
      this.connected = false;
      this.staticTier = true;
      this.epiloguePlaceholder = null;
      this.epilogueOriginalParent = this.epilogue?.parentNode || null;
      this.epilogueOriginalNext = this.epilogue?.nextSibling || null;
      this.rootSnapshot = {
        activeWorld: originalAttribute(root, 'data-active-world'),
        activePhase: originalAttribute(root, 'data-active-phase'),
        state: originalAttribute(root, 'data-state'),
        visualState: originalAttribute(root, 'data-visual-state'),
        tier: originalAttribute(root, 'data-tier'),
        direction: originalAttribute(root, 'data-direction'),
      };
      this.bodyToneBefore = document.body?.getAttribute('data-dv2-header-tone') ?? null;
      this.titleBefore = this.title?.textContent ?? '';
      this.shellSnapshots = new Map();
      [this.headerGroup, this.shortcuts].filter(Boolean).forEach((element) => {
        this.shellSnapshots.set(element, {
          ariaHidden: originalAttribute(element, 'aria-hidden'),
          inert: originalAttribute(element, 'inert'),
          hiddenClass: element.classList.contains('is-r7-cinematic-hidden'),
        });
      });
      this.accessibilitySnapshots = new Map();
      [this.semanticFlow, this.epilogue, this.introActions, ...this.visualCategories].filter(Boolean).forEach((element) => {
        this.accessibilitySnapshots.set(element, {
          ariaHidden: originalAttribute(element, 'aria-hidden'),
          inert: originalAttribute(element, 'inert'),
        });
      });
      this.phraseAttributeSnapshots = new Map();
      this.phrases.forEach((phrase) => {
        this.phraseAttributeSnapshots.set(phrase, {
          position: originalAttribute(phrase, 'data-position'),
          motion: originalAttribute(phrase, 'data-motion'),
          ariaHidden: originalAttribute(phrase, 'aria-hidden'),
          activeClass: phrase.classList.contains('is-active'),
        });
      });
      this.shortcutSnapshots = new Map();
      this.shortcuts?.querySelectorAll('[data-dv2-destination-world]').forEach((item) => {
        this.shortcutSnapshots.set(item, {
          ariaCurrent: originalAttribute(item, 'aria-current'),
          ariaLabel: originalAttribute(item, 'aria-label'),
          currentClass: item.classList.contains('is-current'),
        });
      });
      this.navigationSnapshots = new Map();
      [this.previousButton, this.nextButton].filter(Boolean).forEach((button) => {
        this.navigationSnapshots.set(button, {
          disabled: originalAttribute(button, 'disabled'),
          ariaLabel: originalAttribute(button, 'aria-label'),
          ariaBusy: originalAttribute(button, 'aria-busy'),
        });
      });
      this.navigationCurrentBefore = this.navigationCurrent?.textContent ?? '';
      this.skipLabelSnapshots = new Map(this.skipLabels.map((label) => [label, label.textContent]));
      this.skipAriaLabelBefore = originalAttribute(this.skip, 'aria-label');
      this.skipHiddenBefore = this.skip?.hidden ?? false;
      this.skipVisibleClassBefore = this.skip?.classList.contains('is-visible') ?? false;
      this.introActionsVisibleClassBefore = this.introActions?.classList.contains('is-visible') ?? false;
      this.scrollCueVisibleClassBefore = this.scrollCue?.classList.contains('is-visible') ?? false;
    }

    readConfig() {
      const script = this.root.querySelector('[data-ld-home-film-config]');
      if (!script) {
        this.configDiagnostics.push('config_missing');
        return DEFAULT_CONFIG;
      }

      let source;
      try {
        source = JSON.parse(script.textContent || '{}');
      } catch (_error) {
        this.configDiagnostics.push('config_parse_error');
        return DEFAULT_CONFIG;
      }
      const motionIntensity = ['low', 'balanced', 'full'].includes(source.motionIntensity)
        ? source.motionIntensity
        : (['low', 'balanced', 'full'].includes(this.root.dataset.motionIntensity)
          ? this.root.dataset.motionIntensity
          : DEFAULT_CONFIG.motionIntensity);
      const showSkip = source.showSkip !== false;
      const suppliedTitleEnterScale = finite(source.titleEnterScale, DEFAULT_CONFIG.titleEnterScale);
      const suppliedTitleExitScale = finite(source.titleExitScale, DEFAULT_CONFIG.titleExitScale);
      const visualScalesAreValid = suppliedTitleEnterScale >= 0.65 && suppliedTitleEnterScale <= 0.85 &&
        suppliedTitleExitScale >= 2.6 && suppliedTitleExitScale <= 3.6;
      if (!visualScalesAreValid) this.configDiagnostics.push('config_visual_scale_invalid');
      const titleEnterScale = visualScalesAreValid ? suppliedTitleEnterScale : DEFAULT_CONFIG.titleEnterScale;
      const titleExitScale = visualScalesAreValid ? suppliedTitleExitScale : DEFAULT_CONFIG.titleExitScale;
      const blackPreset = Object.prototype.hasOwnProperty.call(INTRO_PRESET_TIMINGS, source?.intro?.blackPreset)
        ? source.intro.blackPreset
        : DEFAULT_CONFIG.intro.blackPreset;
      const suppliedCategories = Array.isArray(source.categories) ? source.categories : [];
      const requiredNumber = (value) => {
        if (value === null || value === '' || typeof value === 'boolean') return Number.NaN;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : Number.NaN;
      };
      const rawTimeline = {
        ctaTime: requiredNumber(source?.intro?.ctaTime),
        holdTime: requiredNumber(source?.intro?.holdTime),
        desktopTravelVh: requiredNumber(source?.desktopTravelVh),
        mobileTravelVh: requiredNumber(source?.mobileTravelVh),
        epilogue: {
          videoStart: requiredNumber(source?.epilogue?.videoStart),
          blackTarget: requiredNumber(source?.epilogue?.blackTarget),
          videoEnd: requiredNumber(source?.epilogue?.videoEnd),
        },
      };
      const uniqueCategoryIds = new Set(suppliedCategories.map((entry) => entry?.id));
      const structureIsValid = Boolean(source?.intro && source?.epilogue) &&
        suppliedCategories.length === CATEGORY_IDS.length && uniqueCategoryIds.size === CATEGORY_IDS.length &&
        CATEGORY_IDS.every((id) => uniqueCategoryIds.has(id));
      const categories = structureIsValid ? CATEGORY_IDS.map((id) => {
        const candidate = suppliedCategories.find((entry) => entry.id === id);
        return {
          id,
          videoStart: requiredNumber(candidate.videoStart),
          videoEnd: requiredNumber(candidate.videoEnd),
          stableProgress: requiredNumber(candidate.stableProgress),
        };
      }) : [];
      const allNumbers = [
        rawTimeline.ctaTime,
        rawTimeline.holdTime,
        rawTimeline.desktopTravelVh,
        rawTimeline.mobileTravelVh,
        rawTimeline.epilogue.videoStart,
        rawTimeline.epilogue.blackTarget,
        rawTimeline.epilogue.videoEnd,
        ...categories.flatMap((category) => [category.videoStart, category.videoEnd, category.stableProgress]),
      ];
      const continuous = structureIsValid &&
        Math.abs(categories[0].videoStart) <= CONFIG_TOLERANCE &&
        categories.slice(1).every((category, index) => Math.abs(category.videoStart - categories[index].videoEnd) <= CONFIG_TOLERANCE) &&
        Math.abs(rawTimeline.epilogue.videoStart - categories[categories.length - 1].videoEnd) <= CONFIG_TOLERANCE;
      const timingsAreValid = structureIsValid && allNumbers.every(Number.isFinite) && continuous &&
        rawTimeline.ctaTime >= 0 && rawTimeline.holdTime > rawTimeline.ctaTime &&
        categories.every((category) => category.videoStart >= 0 &&
          category.videoEnd > category.videoStart + CONFIG_TOLERANCE &&
          category.videoEnd <= VIDEO_DURATION + CONFIG_TOLERANCE &&
          category.stableProgress >= 0.8 && category.stableProgress <= 0.88) &&
        rawTimeline.epilogue.blackTarget > rawTimeline.epilogue.videoStart + CONFIG_TOLERANCE &&
        rawTimeline.epilogue.videoEnd >= rawTimeline.epilogue.blackTarget &&
        rawTimeline.epilogue.videoEnd <= VIDEO_DURATION + CONFIG_TOLERANCE &&
        rawTimeline.desktopTravelVh >= 100 && rawTimeline.desktopTravelVh <= 145 &&
        rawTimeline.mobileTravelVh >= 115 && rawTimeline.mobileTravelVh <= 165;

      if (!timingsAreValid) {
        this.configDiagnostics.push('config_timeline_invalid');
        return Object.freeze({
          ...DEFAULT_CONFIG,
          showSkip,
          motionIntensity,
          titleEnterScale,
          titleExitScale,
        });
      }

      const boundTime = (value) => clamp(value, 0, VIDEO_DURATION);
      return Object.freeze({
        intro: Object.freeze({
          ctaTime: boundTime(rawTimeline.ctaTime),
          holdTime: boundTime(rawTimeline.holdTime),
          blackPreset,
        }),
        categories: Object.freeze(categories.map((category) => Object.freeze({
          ...category,
          videoStart: boundTime(category.videoStart),
          videoEnd: boundTime(category.videoEnd),
        }))),
        epilogue: Object.freeze({
          videoStart: boundTime(rawTimeline.epilogue.videoStart),
          blackTarget: boundTime(rawTimeline.epilogue.blackTarget),
          videoEnd: boundTime(rawTimeline.epilogue.videoEnd),
        }),
        desktopTravelVh: rawTimeline.desktopTravelVh,
        mobileTravelVh: rawTimeline.mobileTravelVh,
        titleEnterScale,
        titleExitScale,
        showSkip,
        motionIntensity,
      });
    }

    connect() {
      if (this.connected || this.root.dataset.ldHomeFilmReady === 'true') return;
      this.connected = true;
      this.root.dataset.ldHomeFilmReady = 'true';
      this.root.dataset.ldHomeStateOwner = 'ld-home-scroll-film';
      this.root.dataset.ldHomeMediaOwner = 'ld-home-scroll-film';
      this.root.ldHomeFilmController = this;
      CONNECTED.add(this);

      this.bindEvents();
      this.observeLayout();
      this.observeDialogs();
      this.applyTier({ initial: true });
      this.refreshGeometry();
      this.introConsumed = this.staticTier || !this.isAtIntroStart();
      if (!this.staticTier) this.initializeJourneyStateFromScroll();
      if (!this.staticTier) {
        this.loadMediaForViewport({ preserveTime: false });
        if (!this.introConsumed) this.prepareStaticHero();
      }
      this.reportDiagnostics();
      this.inspectMissingMedia();
      this.inspectCommunityResult();
      this.emitOnce('home_view', {
        tier: this.staticTier ? 'static' : 'enhanced',
        reduced_motion: this.reducedMotionQuery.matches,
        save_data: this.saveData,
        motion_intensity: this.config.motionIntensity,
      });
      this.schedule();
      document.dispatchEvent(new CustomEvent('dv2:home:ready', { detail: { controller: this } }));
    }

    bindEvents() {
      if (window.DivettaZorge?.subscribe) {
        const schedulerKey = `home-film-${this.root.dataset.sectionId || Math.random().toString(36).slice(2)}`;
        this.unsubscribeCoordinator = window.DivettaZorge.subscribe(schedulerKey, () => {
          if (this.destroyed || this.isSuspended) return;
          this.inCoordinatorFrame = true;
          try {
            this.onScroll();
            this.sync();
          } finally {
            this.inCoordinatorFrame = false;
          }
        });
      } else {
        window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.signal });
      }
      window.addEventListener('resize', () => this.invalidateGeometry(), { passive: true, signal: this.signal });
      window.addEventListener('orientationchange', () => this.invalidateGeometry(), { passive: true, signal: this.signal });
      window.addEventListener('wheel', (event) => this.onJourneyWheel(event), { capture: true, passive: false, signal: this.signal });
      window.addEventListener('touchstart', (event) => this.onJourneyTouchStart(event), { capture: true, passive: true, signal: this.signal });
      window.addEventListener('touchmove', (event) => this.onJourneyTouchMove(event), { capture: true, passive: false, signal: this.signal });
      window.addEventListener('touchend', () => this.onJourneyTouchEnd(), { capture: true, passive: true, signal: this.signal });
      window.addEventListener('touchcancel', () => this.onJourneyTouchEnd(), { capture: true, passive: true, signal: this.signal });
      window.addEventListener('pagehide', () => this.suspend('pagehide'), { signal: this.signal });
      window.addEventListener('pageshow', (event) => {
        this.resume('pagehide');
        this.resume('route');
        this.invalidateGeometry();
        if (event.persisted) this.loadMediaForViewport({ preserveTime: true });
        this.resetCommunitySubmit();
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.suspend('hidden');
        else {
          this.resume('hidden');
          this.invalidateGeometry();
        }
      }, { signal: this.signal });
      document.addEventListener('ld:assistant:state', (event) => this.onAssistantState(event), { signal: this.signal });
      document.addEventListener('ld:home-suspend', (event) => this.onHomeSuspend(event), { signal: this.signal });
      document.addEventListener('ld:home-resume', (event) => this.onHomeResume(event), { signal: this.signal });
      document.addEventListener('ld:menu-state', (event) => {
        if (event.detail?.open) this.suspend('menu');
        else this.resume('menu');
      }, { signal: this.signal });
      document.addEventListener('toggle', () => this.syncDialogSuspension(), { capture: true, signal: this.signal });
      document.addEventListener('close', () => this.syncDialogSuspension(), { capture: true, signal: this.signal });
      document.addEventListener('cancel', () => this.syncDialogSuspension(), { capture: true, signal: this.signal });
      document.addEventListener('click', (event) => this.onDocumentRouteClick(event), { capture: true, signal: this.signal });
      this.root.addEventListener('click', (event) => this.onRootClick(event), { signal: this.signal });
      this.root.addEventListener('focusin', (event) => this.onFocusIn(event), { signal: this.signal });
      this.root.addEventListener('focusout', (event) => this.onFocusOut(event), { signal: this.signal });
      this.root.addEventListener('pointerover', (event) => this.onPointerOver(event), { signal: this.signal });
      this.root.addEventListener('pointerout', (event) => this.onPointerOut(event), { signal: this.signal });
      this.root.querySelectorAll('.ld-home-film__community-form, .ld-community-inline-v3__form').forEach((form) => {
        form.addEventListener('submit', (event) => this.onCommunitySubmit(event), { signal: this.signal });
      });

      if (this.video) {
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.playsInline = true;
        this.video.preload = 'none';
        this.video.addEventListener('loadedmetadata', () => this.onLoadedMetadata(), { signal: this.signal });
        this.video.addEventListener('loadeddata', () => this.root.classList.add('has-video-frame'), { signal: this.signal });
        this.video.addEventListener('seeking', () => this.root.classList.add('is-video-seeking'), { signal: this.signal });
        this.video.addEventListener('seeked', () => this.onSeeked(), { signal: this.signal });
        this.video.addEventListener('error', () => this.useVideoFallback('media_error'), { signal: this.signal });
        this.video.addEventListener('timeupdate', () => {
          if (this.introPlaying) this.monitorIntro();
          if (this.automaticPlayback?.kind === 'journey') this.monitorJourneyMarker();
        }, { signal: this.signal });
      }

      const refreshEnvironment = () => {
        const wasMobile = this.root.dataset.mediaViewport === 'mobile';
        const isMobile = this.mobileQuery.matches;
        this.applyTier();
        this.invalidateGeometry();
        if (!this.staticTier && wasMobile !== isMobile) this.loadMediaForViewport({ preserveTime: true });
      };
      [this.reducedMotionQuery, this.shortViewportQuery, this.mobileQuery, this.hoverQuery].forEach((query) => {
        if (typeof query.addEventListener === 'function') {
          query.addEventListener('change', refreshEnvironment);
          this.mediaQueryCleanups.push(() => query.removeEventListener('change', refreshEnvironment));
        } else if (typeof query.addListener === 'function') {
          query.addListener(refreshEnvironment);
          this.mediaQueryCleanups.push(() => query.removeListener(refreshEnvironment));
        }
      });
      if (this.connection) {
        if (typeof this.connection.addEventListener === 'function') {
          this.connection.addEventListener('change', refreshEnvironment);
          this.mediaQueryCleanups.push(() => this.connection.removeEventListener('change', refreshEnvironment));
        }
      }
    }

    observeLayout() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.invalidateGeometry());
        [this.root, this.track, this.stage, this.semanticFlow].filter(Boolean).forEach((element) => this.resizeObserver.observe(element));
      }
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          const entry = entries.find((candidate) => candidate.target === this.root);
          if (!entry) return;
          if (entry.isIntersecting) this.resume('outside');
          else this.suspend('outside');
        }, { rootMargin: '25% 0px 25% 0px', threshold: 0 });
        this.intersectionObserver.observe(this.root);
      }
    }

    observeDialogs() {
      const ids = new Set();
      this.root.querySelectorAll('[data-ld-cert-open][aria-controls]').forEach((trigger) => {
        const id = trigger.getAttribute('aria-controls');
        if (id) ids.add(id);
      });
      this.knownDialogs = Array.from(new Set([
        ...Array.from(ids).map((id) => document.getElementById(id)).filter(Boolean),
        ...Array.from(this.root.querySelectorAll('dialog')),
      ]));
      if ('MutationObserver' in window && this.knownDialogs.length) {
        this.dialogObserver = new MutationObserver(() => this.syncDialogSuspension());
        this.knownDialogs.forEach((dialog) => this.dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
      }
    }

    reportDiagnostics() {
      this.configDiagnostics.forEach((reason) => {
        console.warn(`[LDHomeFilm] ${reason}; safe defaults are active.`);
        this.emitOnce('home_config_invalid', { reason, config_status: 'defaults' }, `diagnostic:${reason}`);
      });
      if (!this.hasCompleteChoreography()) {
        console.warn('[LDHomeFilm] required_structure_missing; static semantic flow retained.');
        this.emitOnce('home_asset_missing', { asset_type: 'structure', reason: 'required_structure_missing' }, 'diagnostic:structure');
      }
    }

    inspectMissingMedia() {
      if (!this.video) {
        this.emitOnce('home_asset_missing', { asset_type: 'video', reason: 'video_element_missing' }, 'missing:video-element');
      } else if (!this.video.dataset.srcDesktop && !this.video.dataset.srcMobile) {
        this.emitOnce('home_asset_missing', { asset_type: 'video', reason: 'video_source_missing' }, 'missing:video-source');
      }
      CATEGORY_IDS.forEach((category) => {
        const visual = this.visualFor(category);
        if (!visual || visual.dataset.missingMedia === 'true' || visual.classList.contains('is-missing-media')) {
          this.emitOnce('home_asset_missing', { asset_type: 'category_image', category }, `missing:image:${category}`);
        }
      });
    }

    inspectCommunityResult() {
      const success = this.root.querySelector('[data-ld-community-success], .ld-community-inline-v3__success');
      const error = this.root.querySelector('.ld-home-film__community-form .ld-home-film__form-message.is-error, .ld-community-inline-v3__error');
      if (success) {
        this.emitOnce('community_submit', { result: 'success' }, 'community:success');
        this.later(() => success.focus?.({ preventScroll: true }), 0);
      } else if (error) {
        this.emitOnce('community_submit', { result: 'error' }, 'community:error');
      }
    }

    get saveData() {
      return Boolean(this.connection?.saveData);
    }

    get lowCapability() {
      if (window.DivettaZorge?.capabilities) return Boolean(window.DivettaZorge.capabilities.lowCapability);
      const memory = Number(navigator.deviceMemory || 0);
      const processors = Number(navigator.hardwareConcurrency || 0);
      return (memory > 0 && memory <= 4) || (processors > 0 && processors <= 4);
    }

    hasCompleteChoreography() {
      return Boolean(this.track && this.stage && this.video && this.identity && this.semanticFlow && this.curtain && this.title &&
        CATEGORY_IDS.every((category) => this.visualFor(category) &&
          this.semanticCategories.some((section) => section.dataset.category === category) &&
          [1, 2, 3, 4].every((index) => this.phrases.some((phrase) => phrase.dataset.category === category && finite(phrase.dataset.index, 0) === index))));
    }

    shouldUseStaticTier() {
      return this.forcedStatic || this.designMode || this.reducedMotionQuery.matches || !this.hasCompleteChoreography();
    }

    applyTier({ initial = false } = {}) {
      const useStatic = this.shouldUseStaticTier();
      const changed = useStatic !== this.staticTier || initial;
      const worldBeforeChange = !initial && PUBLIC_WORLDS.includes(this.activeWorld) ? this.activeWorld : null;
      this.staticTier = useStatic;
      this.root.classList.toggle('is-enhanced', !useStatic);
      this.root.classList.toggle('is-static', useStatic);
      this.root.classList.toggle('is-reduced-motion', this.reducedMotionQuery.matches);
      this.root.classList.toggle('is-low-data', this.saveData);
      this.root.classList.toggle('is-low-capability', this.lowCapability);
      this.root.classList.toggle('is-short-viewport', this.shortViewportQuery.matches);
      this.root.classList.toggle('is-restrained-effects', this.saveData || this.lowCapability || this.shortViewportQuery.matches);
      this.root.classList.toggle('is-design-mode', this.designMode);
      this.root.classList.toggle('is-duplicate-home', this.forcedStatic);
      this.root.dataset.tier = useStatic ? 'static' : 'enhanced';
      this.root.dataset.mediaViewport = this.mobileQuery.matches ? 'mobile' : 'desktop';
      this.root.dataset.motionIntensity = this.config.motionIntensity;
      this.updatePosterForViewport();

      if (!changed) return;
      this.cancelAutomaticPlayback('tier_change');
      this.cancelIntro();
      if (useStatic) {
        this.pauseIntroSequence();
        this.introConsumed = true;
        this.setSemanticEnhanced(false);
        if (this.reducedMotionQuery.matches && this.hasCompleteChoreography()) {
          this.video?.pause();
          this.video?.removeAttribute('autoplay');
        } else {
          this.unloadMedia();
        }
        this.setSkipVisible(false);
        this.setAllVisualCategoriesInactive();
        if (worldBeforeChange) this.restoreStaticWorldAfterTierChange(worldBeforeChange);
      } else {
        this.setSemanticEnhanced(true);
        if (!initial) {
          this.introConsumed = true;
          this.geometryDirty = true;
          this.refreshGeometry();
          if (worldBeforeChange) this.restoreEnhancedWorldAfterTierChange(worldBeforeChange);
          this.loadMediaForViewport({ preserveTime: true });
        }
      }
      this.invalidateGeometry();
    }

    restoreStaticWorldAfterTierChange(world) {
      const target = world === 'epilogue'
        ? this.epilogue
        : this.semanticCategories.find((section) => section.dataset.category === world);
      if (!target) return;
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.previousScrollY = window.scrollY;
      const stableProgress = world === 'epilogue'
        ? 1
        : this.config.categories[CATEGORY_IDS.indexOf(world)]?.stableProgress || V2_PHASES.categoryStable;
      this.setActive(world, world === 'epilogue' ? 'epilogue-stable' : 'chapter-stable', stableProgress, world === 'epilogue' ? 'epilogue' : 'stable');
    }

    restoreEnhancedWorldAfterTierChange(world) {
      const target = this.targetForWorld(world);
      if (!Number.isFinite(target)) return;
      this.previousScrollY = target;
      window.scrollTo({ top: target, behavior: 'instant' });
      if (world === 'epilogue') {
        this.lastSegmentIndex = CATEGORY_IDS.length;
        this.targetVideoTime = this.config.epilogue.blackTarget;
        this.setActive(world, 'epilogue-stable', 1, 'epilogue');
      } else {
        const index = CATEGORY_IDS.indexOf(world);
        this.lastSegmentIndex = index;
        const category = this.config.categories[index];
        this.targetVideoTime = this.videoTimeForCategory(category, category.stableProgress);
        this.setActive(world, 'chapter-stable', this.config.categories[index].stableProgress, 'stable');
      }
    }

    setSemanticEnhanced(enhanced) {
      if (!this.semanticFlow) return;
      if (enhanced) {
        this.semanticFlow.setAttribute('inert', '');
        this.semanticFlow.setAttribute('aria-hidden', 'true');
      } else {
        this.semanticFlow.removeAttribute('inert');
        this.semanticFlow.removeAttribute('aria-hidden');
      }
      if (!enhanced && this.epilogue) {
        this.epilogue.removeAttribute('inert');
        this.epilogue.removeAttribute('aria-hidden');
      }
    }

    moveEpilogueIntoStage() {
      if (!this.epilogue || !this.stage || this.stage.contains(this.epilogue)) return;
      if (!this.epiloguePlaceholder) {
        this.epiloguePlaceholder = document.createComment('ld-home-epilogue-origin');
        this.epilogue.parentNode?.insertBefore(this.epiloguePlaceholder, this.epilogue);
      }
      this.stage.appendChild(this.epilogue);
      this.epilogue.setAttribute('inert', '');
      this.epilogue.setAttribute('aria-hidden', 'true');
    }

    restoreEpilogue() {
      if (!this.epilogue || !this.epiloguePlaceholder?.parentNode) return;
      this.epiloguePlaceholder.parentNode.insertBefore(this.epilogue, this.epiloguePlaceholder);
      this.epiloguePlaceholder.remove();
      this.epiloguePlaceholder = null;
    }

    headerHeight() {
      const styles = getComputedStyle(document.documentElement);
      const value = styles.getPropertyValue('--header-group-height') || styles.getPropertyValue('--header-height');
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }

    refreshGeometry() {
      if (!this.track || !this.stage) {
        this.geometry = null;
        this.geometryDirty = false;
        return;
      }
      const scrollY = window.scrollY;
      const trackRect = this.track.getBoundingClientRect();
      const stageRect = this.stage.getBoundingClientRect();
      const trackTop = trackRect.top + scrollY;
      const stageHeight = Math.max(1, stageRect.height || window.innerHeight - this.headerHeight());
      const scrollStart = Math.max(0, trackTop - this.headerHeight());
      const scrollable = Math.max(1, trackRect.height - stageHeight);
      const viewport = Math.max(1, window.innerHeight);
      const chapterVh = this.mobileQuery.matches ? this.config.mobileTravelVh : this.config.desktopTravelVh;
      const heroVh = this.mobileQuery.matches ? 48 : 42;
      const epilogueVh = this.epilogue ? (this.mobileQuery.matches ? 100 : 90) : 0;
      const desiredLengths = [heroVh, chapterVh, chapterVh, chapterVh, chapterVh, epilogueVh].map((vh) => viewport * vh / 100);
      const desiredTotal = desiredLengths.reduce((sum, value) => sum + value, 0);
      const scale = scrollable / Math.max(1, desiredTotal);
      let cursor = scrollStart;
      const intro = { start: cursor, length: desiredLengths[0] * scale };
      intro.end = intro.start + intro.length;
      cursor = intro.end;
      const categories = CATEGORY_IDS.map((id, index) => {
        const length = desiredLengths[index + 1] * scale;
        const item = { id, index, start: cursor, length, end: cursor + length };
        cursor = item.end;
        return item;
      });
      const epilogue = { start: cursor, length: desiredLengths[5] * scale, end: cursor + desiredLengths[5] * scale };
      this.geometry = Object.freeze({
        trackTop,
        trackHeight: trackRect.height,
        stageHeight,
        scrollStart,
        scrollEnd: scrollStart + scrollable,
        scrollable,
        intro: Object.freeze(intro),
        categories: Object.freeze(categories.map((item) => Object.freeze(item))),
        epilogue: Object.freeze(epilogue),
      });
      this.geometryDirty = false;
    }

    invalidateGeometry() {
      this.geometryDirty = true;
      if (!this.automaticPlayback) this.schedule();
    }

    stableJourneyStateForWorld(world) {
      return ({
        viso: JOURNEY_STATES.viso,
        capelli: JOURNEY_STATES.capelli,
        corpo: JOURNEY_STATES.corpo,
        makeup: JOURNEY_STATES.makeup,
      })[world] || null;
    }

    playingJourneyStateForWorld(world) {
      return ({
        viso: JOURNEY_STATES.playingViso,
        capelli: JOURNEY_STATES.playingCapelli,
        corpo: JOURNEY_STATES.playingCorpo,
        makeup: JOURNEY_STATES.playingMakeup,
      })[world] || null;
    }

    worldForJourneyState(state = this.journeyState) {
      return ({
        [JOURNEY_STATES.viso]: 'viso',
        [JOURNEY_STATES.capelli]: 'capelli',
        [JOURNEY_STATES.corpo]: 'corpo',
        [JOURNEY_STATES.makeup]: 'makeup',
      })[state] || null;
    }

    setJourneyState(state) {
      if (!JOURNEY_STATE_VALUES.has(state)) return false;
      this.journeyState = state;
      this.transitionInProgress = state.startsWith('PLAYING_TO_');
      this.root.dataset.journeyState = state;
      this.root.dataset.transitionInProgress = this.transitionInProgress ? 'true' : 'false';
      this.setCinematicShellHidden(this.transitionInProgress);
      return true;
    }

    setCinematicShellHidden(hidden) {
      this.shellSnapshots.forEach((snapshot, element) => {
        element.classList.toggle('is-r7-cinematic-hidden', hidden || snapshot.hiddenClass);
        if (hidden) {
          element.setAttribute('aria-hidden', 'true');
          element.setAttribute('inert', '');
          return;
        }
        restoreAttribute(element, 'aria-hidden', snapshot.ariaHidden);
        restoreAttribute(element, 'inert', snapshot.inert);
      });
    }

    initializeJourneyStateFromScroll() {
      if (this.staticTier) return;
      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return;
      const scrollY = window.scrollY;
      this.journeyReleased = scrollY >= this.geometry.epilogue.start;
      if (scrollY < this.geometry.intro.end) {
        this.setJourneyState(JOURNEY_STATES.introReady);
        this.journeyAnchorY = Math.max(this.geometry.intro.start, Math.min(scrollY, this.geometry.intro.end));
        return;
      }
      const geometry = this.geometry.categories.find((category) => scrollY < category.end) || this.geometry.categories[this.geometry.categories.length - 1];
      const category = this.config.categories[geometry.index];
      const stableState = this.stableJourneyStateForWorld(category.id);
      this.setJourneyState(stableState);
      this.journeyAnchorY = scrollTargetAtOrAfter(geometry, category.stableProgress);
      this.lastSegmentIndex = geometry.index;
      this.activeCategoryIndex = geometry.index;
      this.targetVideoTime = category.videoEnd;
      this.setActive(category.id, 'chapter-stable', category.stableProgress, 'stable');
      this.writeChapterStyles(category.stableProgress, category.stableProgress);
      this.renderPhrase(category.id, 1);
      this.renderTitle(category.id, 0);
      this.renderVisualCategory(category.id, true, true);
    }

    isJourneyInputActive() {
      if (this.destroyed || this.staticTier || this.isSuspended || this.designMode || this.mediaFallback) return false;
      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return false;
      const scrollY = window.scrollY;
      return scrollY >= this.geometry.scrollStart - 6 && scrollY <= this.geometry.epilogue.start + 6;
    }

    scrollToJourneyAnchor(target = this.journeyAnchorY) {
      if (!Number.isFinite(target)) return;
      this.journeyAnchorY = Math.max(0, target);
      if (Math.abs(window.scrollY - this.journeyAnchorY) <= 1) {
        this.previousScrollY = window.scrollY;
        return;
      }
      this.journeyInternalScroll = true;
      this.previousScrollY = this.journeyAnchorY;
      window.scrollTo({ top: this.journeyAnchorY, behavior: 'instant' });
      requestAnimationFrame(() => { this.journeyInternalScroll = false; });
    }

    requestJourneyStep(direction, method = 'scroll') {
      if (!['next', 'previous'].includes(direction) || this.staticTier || this.isSuspended) return false;
      if (this.transitionInProgress || this.automaticPlayback) return true;
      const currentWorld = this.worldForJourneyState();

      // SINGLE-OWNER RETURN PATH — migrated from the former files-master adapter.
      // Backward navigation restores the previous stable world with one deterministic seek
      // instead of creating a second reverse-playback controller.
      if (direction === 'previous') {
        const currentIndex = CATEGORY_IDS.indexOf(currentWorld);
        if (currentIndex < 0) return false;
        this.video?.pause();
        this.resetVideoPlaybackRate?.();
        this.cancelAutomaticPlayback?.('previous_stable_jump', { silent: true });
        this.journeyReleased = false;

        if (currentIndex === 0) {
          return this.returnToIntroReadyV11?.() || false;
        }

        const targetWorld = CATEGORY_IDS[currentIndex - 1];
        const targetCategory = this.config?.categories?.find?.((category) => category.id === targetWorld);
        if (!targetCategory) return false;
        this.emit?.('chapter_navigation_previous', {
          category: targetWorld,
          from_state: this.journeyState,
          method: `${method}-stable`,
          world_id: targetWorld,
        });
        const restored = this.jumpToStableWorld?.(targetWorld);
        if (!restored) return false;
        const targetTime = Math.max(targetCategory.videoStart, targetCategory.videoEnd - FRAME_DURATION);
        this.targetVideoTime = targetTime;
        this.seekVideo?.(targetTime, { force: true });
        this.root.dataset.previousNavigationMode = 'canonical-stable-single-seek';
        return true;
      }

      let targetWorld = null;
      if (this.journeyState === JOURNEY_STATES.introReady) targetWorld = CATEGORY_IDS[0];
      else if (currentWorld) {
        const index = CATEGORY_IDS.indexOf(currentWorld);
        if (index >= 0 && index < CATEGORY_IDS.length - 1) targetWorld = CATEGORY_IDS[index + 1];
      }
      if (!targetWorld) return false;
      this.journeyReleased = false;
      return this.startJourneyTransition({ direction, sourceWorld: currentWorld || 'handoff', targetWorld, method });
    }

    startJourneyTransition({ direction, sourceWorld, targetWorld, method }) {
      const targetIndex = CATEGORY_IDS.indexOf(targetWorld);
      const targetCategory = this.config.categories[targetIndex];
      if (!targetCategory || this.transitionInProgress || this.automaticPlayback) return false;
      const forward = direction === 'next';
      const sourceIndex = CATEGORY_IDS.indexOf(sourceWorld);
      const sourceCategory = sourceIndex >= 0 ? this.config.categories[sourceIndex] : null;
      if (!forward && !sourceCategory) return false;
      const mediaStart = forward ? targetCategory.videoStart : sourceCategory.videoEnd;
      const mediaEnd = forward ? targetCategory.videoEnd : sourceCategory.videoStart;
      if (!Number.isFinite(mediaStart) || !Number.isFinite(mediaEnd) || Math.abs(mediaEnd - mediaStart) <= CONFIG_TOLERANCE) return false;
      const playback = {
        kind: forward ? 'journey' : 'return',
        direction,
        sourceWorld,
        targetWorld,
        renderWorld: forward ? targetWorld : sourceWorld,
        mediaStart,
        mediaEnd,
        totalDuration: Math.abs(mediaEnd - mediaStart) * 1000 * (forward ? CHAPTER_TIME_SCALE : 1),
        targetY: this.targetForWorld(targetWorld),
        sourceY: this.journeyAnchorY,
        method,
        mediaMode: 'pending',
        startedAt: null,
        expectedVideoTime: mediaStart,
      };
      this.emit(direction === 'next' ? 'chapter_navigation_next' : 'chapter_navigation_previous', {
        category: targetWorld,
        from_state: this.journeyState,
        method,
        world_id: targetWorld,
      });
      return this.beginAutomaticPlayback(playback);
    }

    onJourneyWheel(event) {
      if (!this.isJourneyInputActive()) return;
      const direction = event.deltaY > 0 ? 'next' : event.deltaY < 0 ? 'previous' : null;
      if (!direction) return;
      if (this.transitionInProgress || this.automaticPlayback) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      if (direction === 'next' && this.journeyState === JOURNEY_STATES.makeup) {
        this.journeyReleased = true;
        return;
      }
      if (direction === 'previous' && this.journeyState === JOURNEY_STATES.introReady) return;
      if (this.requestJourneyStep(direction, 'wheel') && event.cancelable) event.preventDefault();
    }

    onJourneyTouchStart(event) {
      if (!this.isJourneyInputActive() || event.touches?.length !== 1) {
        this.touchStartY = null;
        this.touchHandled = false;
        return;
      }
      this.touchStartY = event.touches[0].clientY;
      this.touchHandled = false;
    }

    onJourneyTouchMove(event) {
      if (!this.isJourneyInputActive()) return;
      if (this.transitionInProgress || this.automaticPlayback) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      if (this.touchHandled || this.touchStartY === null || event.touches?.length !== 1) return;
      const delta = this.touchStartY - event.touches[0].clientY;
      if (Math.abs(delta) < TOUCH_INTENT_THRESHOLD) return;
      const direction = delta > 0 ? 'next' : 'previous';
      if (direction === 'next' && this.journeyState === JOURNEY_STATES.makeup) {
        this.journeyReleased = true;
        this.touchHandled = true;
        return;
      }
      if (direction === 'previous' && this.journeyState === JOURNEY_STATES.introReady) return;
      if (this.requestJourneyStep(direction, 'touch')) {
        this.touchHandled = true;
        if (event.cancelable) event.preventDefault();
      }
    }

    onJourneyTouchEnd() {
      this.touchStartY = null;
      this.touchHandled = false;
    }

    isAtIntroStart() {
      if (!this.geometry) return window.scrollY <= 4;
      return window.scrollY <= this.geometry.intro.start + 6;
    }

    onScroll() {
      if (this.staticTier) {
        this.schedule();
        return;
      }
      if (this.journeyInternalScroll) {
        this.previousScrollY = window.scrollY;
        return;
      }
      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return;

      if (this.journeyReleased) {
        if (window.scrollY >= this.geometry.epilogue.start - 2) this.schedule();
        else if (window.scrollY <= (this.targetForWorld('makeup') || 0) + 2) {
          this.journeyReleased = false;
          this.setJourneyState(JOURNEY_STATES.makeup);
          this.journeyAnchorY = this.targetForWorld('makeup') || window.scrollY;
        }
        this.previousScrollY = window.scrollY;
        return;
      }

      const delta = window.scrollY - this.journeyAnchorY;
      if (Math.abs(delta) <= 3) {
        this.previousScrollY = window.scrollY;
        return;
      }
      if (this.transitionInProgress || this.automaticPlayback) {
        this.scrollToJourneyAnchor();
        return;
      }
      const direction = delta > 0 ? 'next' : 'previous';
      if (direction === 'next' && this.journeyState === JOURNEY_STATES.makeup) {
        this.journeyReleased = true;
        this.previousScrollY = window.scrollY;
        return;
      }
      if (direction === 'previous' && this.journeyState === JOURNEY_STATES.introReady) {
        this.previousScrollY = window.scrollY;
        return;
      }
      this.scrollToJourneyAnchor();
      this.requestJourneyStep(direction, 'scroll');
    }

    schedule() {
      if (this.destroyed || this.isSuspended) return;
      if (this.unsubscribeCoordinator) {
        if (!this.inCoordinatorFrame) window.DivettaZorge?.requestFrame?.();
        return;
      }
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    }

    sync() {
      if (this.destroyed || this.isSuspended) return;
      if (this.geometryDirty) this.refreshGeometry();
      if (this.staticTier) {
        this.syncStatic();
        return;
      }
      if (!this.geometry || this.automaticPlayback || this.transitionInProgress) return;

      if (this.epilogue && this.journeyReleased && this.geometry.epilogue.length > 0 && window.scrollY >= this.geometry.epilogue.start - 2) {
        const progress = clamp((window.scrollY - this.geometry.epilogue.start) / this.geometry.epilogue.length);
        this.renderEpilogue(progress);
        return;
      }

      if (this.journeyState === JOURNEY_STATES.introReady) return;
      const world = this.worldForJourneyState();
      if (!world) return;
      const index = CATEGORY_IDS.indexOf(world);
      const category = this.config.categories[index];
      if (!category) return;
      this.lastSegmentIndex = index;
      this.renderForward(index, category.stableProgress);
    }

    syncStatic() {
      const viewportTop = this.headerHeight();
      const viewportBottom = window.innerHeight;
      let best = null;
      this.semanticCategories.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const visible = Math.max(0, Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop));
        if (visible > 0 && (!best || visible > best.visible)) best = { section, visible };
      });
      const epilogueRect = this.epilogue?.getBoundingClientRect();
      const epilogueVisible = epilogueRect
        ? Math.max(0, Math.min(epilogueRect.bottom, viewportBottom) - Math.max(epilogueRect.top, viewportTop))
        : 0;
      if (epilogueVisible > (best?.visible || 0)) {
        this.setEpilogueActive(true);
        this.setActive('epilogue', 'epilogue-stable', 1, 'epilogue');
        this.emitOnce('epilogue_view', {}, 'epilogue:view');
      } else if (best) {
        this.setEpilogueActive(false);
        const category = safeCategory(best.section.dataset.category);
        if (category) {
          const stableProgress = this.config.categories[CATEGORY_IDS.indexOf(category)]?.stableProgress || V2_PHASES.categoryStable;
          this.setActive(category, 'chapter-stable', stableProgress, 'stable');
        }
      } else {
        this.setEpilogueActive(false);
        this.setActive('handoff', INTRO_STATES.hold, 0, '');
        this.revealIntroControls();
      }
      this.setSkipVisible(false);
    }

    prepareStaticHero() {
      if (this.destroyed || this.introConsumed || this.isSuspended) return;
      this.cancelIntro();
      this.pauseIntroSequence();
      this.completeIntroTitle('static_hero');
      this.revealIntroControls();
      this.setIntroState(INTRO_STATES.hold);
      this.targetVideoTime = HERO_STILL_TIME;
      this.writeStyle('--ld-film-hero-crossfade', 0);
      this.video?.pause();
      if (this.mediaReady) this.seekVideo(HERO_STILL_TIME, { force: true });
    }

    beginIntroSequence() {
      if (this.destroyed || this.staticTier || this.introConsumed || this.isSuspended || this.introSequenceCompleted) return;
      if (!this.introSequenceStarted) {
        this.introSequenceStarted = true;
        this.introSequenceElapsed = 0;
        this.introState = INTRO_STATES.black;
      }
      if (this.introSequenceRunning) return;
      this.introSequenceRunning = true;
      this.introSequenceStartedAt = performance.now();
      this.monitorIntroSequence();
    }

    pauseIntroSequence() {
      if (this.introSequenceRunning) {
        this.introSequenceElapsed += Math.max(0, performance.now() - this.introSequenceStartedAt);
      }
      this.introSequenceRunning = false;
      this.introSequenceStartedAt = 0;
      if (this.introSequenceFrame) cancelAnimationFrame(this.introSequenceFrame);
      this.introSequenceFrame = 0;
    }

    completeIntroTitle(method = 'kinetic') {
      if (this.introSequenceCompleted) return;
      this.introSequenceCompleted = true;
      this.pauseIntroSequence();
      this.root.classList.add('has-intro-title-complete');
      this.emitOnce('intro_title_complete', { method }, 'intro:title-complete');
    }

    setIntroState(state) {
      if (!Object.values(INTRO_STATES).includes(state)) return;
      this.introState = state;
      if (this.activeWorld === 'handoff') this.setActive('handoff', state, this.activeLocalProgress, '');
    }

    monitorIntroSequence() {
      if (!this.introSequenceRunning || this.destroyed || this.introConsumed || this.isSuspended) return;
      const elapsed = this.introSequenceElapsed + Math.max(0, performance.now() - this.introSequenceStartedAt);
      const timings = INTRO_PRESET_TIMINGS[this.config.intro.blackPreset] || INTRO_TIMINGS;
      if (elapsed < timings.blackEnd) {
        this.setIntroState(INTRO_STATES.black);
      } else if (elapsed < timings.titleHoldEnd) {
        this.setIntroState(INTRO_STATES.titleReveal);
      } else {
        this.setIntroState(INTRO_STATES.titleToFilm);
      }

      if (elapsed >= 820) this.revealIntroControls();

      if (elapsed >= timings.titleToFilmEnd) {
        this.completeIntroTitle('kinetic');
        this.setIntroState(INTRO_STATES.hold);
        return;
      }

      this.introSequenceFrame = requestAnimationFrame(() => {
        this.introSequenceFrame = 0;
        this.monitorIntroSequence();
      });
    }

    renderIntro(progress) {
      this.activeCategoryIndex = -1;
      this.setAllVisualCategoriesInactive();
      this.setEpilogueActive(false);
      const state = this.introPlaying
        ? INTRO_STATES.autoplay
        : (this.introState === INTRO_STATES.scroll
          ? INTRO_STATES.scroll
          : (this.introControlsRevealed && this.introConsumed ? INTRO_STATES.hold : this.introState));
      this.setActive('handoff', state, progress, '');
      this.writeStyle('--ld-film-intro-progress', round(progress));
      const crossfade = easeInOutCubic(segment(progress, 0, 0.18));
      this.writeStyle('--ld-film-hero-crossfade', round(crossfade));
      this.writeChapterStyles(0);
      const scrolled = progress > 0.002;
      this.identity?.classList.toggle('is-active', true);
      this.setIntroControls(!scrolled && this.introControlsRevealed);
      if (!this.introConsumed && !this.introPlaying && this.isAtIntroStart()) this.prepareStaticHero();
      this.targetVideoTime = lerp(HERO_STILL_TIME, this.config.intro.holdTime, easeInOutCubic(clamp(progress)));
      this.seekVideo(this.targetVideoTime, { force: scrolled && progress < 0.03 });
    }

    renderForward(index, progress) {
      const category = this.config.categories[index];
      if (!category) return;
      if (this.manualVideoAnchor && this.manualVideoAnchor.categoryId !== category.id) this.manualVideoAnchor = null;
      this.activeCategoryIndex = index;
      this.identity?.classList.remove('is-active');
      this.setIntroControls(false);
      this.setEpilogueActive(false);
      const state = this.stateForChapterProgress(progress, category.stableProgress);
      const visualState = this.visualStateForChapterState(state);
      const enteredStable = state === 'chapter-stable' &&
        (this.activeState !== 'chapter-stable' || this.activeWorld !== category.id);
      this.setActive(category.id, state, progress, visualState);
      this.writeStyle('--ld-film-intro-progress', 1);
      this.writeStyle('--ld-film-hero-crossfade', 1);
      this.writeChapterStyles(progress, category.stableProgress);
      this.renderPhrase(category.id, progress);
      if (!this.automaticPlayback?.contractV11) this.renderTitle(category.id, progress);
      const previousCategory = index > 0 ? CATEGORY_IDS[index - 1] : '';
      if (!this.automaticPlayback?.contractV11 || !this.automaticPlayback.portalHandoff) {
        if (previousCategory && progress < V2_PHASES.previousExitEnd) {
          this.renderVisualCategory(previousCategory, true, false);
        } else {
          this.renderVisualCategory(category.id, progress >= V2_PHASES.categoryRevealStart, progress >= category.stableProgress);
        }
      }
      const skipPhase = state.startsWith('chapter-phrase-') ||
        state === 'chapter-title-enter' || state === 'chapter-title-exit' || state === 'chapter-reveal';
      this.setSkipVisible(this.config.showSkip && progress < category.stableProgress && skipPhase);
      const automaticJourney = Boolean(this.automaticPlayback && ['journey', 'return'].includes(this.automaticPlayback.kind));
      if (automaticJourney) {
        this.targetVideoTime = this.automaticPlayback.expectedVideoTime ?? category.videoStart;
      } else {
        this.targetVideoTime = this.videoTimeForCategory(category, progress);
        this.seekVideo(this.targetVideoTime, { force: enteredStable });
        if (progress >= category.stableProgress) this.manualVideoAnchor = null;
      }

      const phraseIndex = this.phraseIndexForProgress(progress);
      if (phraseIndex) this.emitOnce('phrase_impression', { category: category.id, phrase_index: phraseIndex }, `phrase:${category.id}:${phraseIndex}`);
      if (progress >= V2_PHASES.titleEnterStart) this.emitOnce('chapter_title_impression', { category: category.id }, `title:${category.id}`);
      if (progress >= V2_PHASES.categoryRevealStart) this.emitOnce('category_reveal', { category: category.id }, `reveal:${category.id}`);
      if (state === 'chapter-stable' && (enteredStable || this.pendingStableAnnouncement === category.id)) {
        this.announce(`Capitolo ${this.titleFor(category.id)} pronto`);
        this.pendingStableAnnouncement = null;
      }
    }

    renderEpilogue(progress) {
      this.activeCategoryIndex = CATEGORY_IDS.length;
      this.identity?.classList.remove('is-active');
      this.setIntroControls(false);
      this.setAllVisualCategoriesInactive();
      this.renderPhrase('', 1);
      this.renderTitle('', 0);
      this.setSkipVisible(false);
      const portalReady = progress >= V2_PHASES.epilogueStable;
      const state = portalReady ? 'epilogue-stable' : 'epilogue-reveal';
      const enteredStable = portalReady && this.activeState !== 'epilogue-stable';
      this.setActive('epilogue', state, progress, portalReady ? 'epilogue' : 'film-to-black');
      this.setEpilogueActive(portalReady);
      this.writeStyle('--ld-film-intro-progress', 1);
      this.writeChapterStyles(1);
      const filmProgress = segment(progress, 0, V2_PHASES.epilogueStable);
      const automaticJourney = this.automaticPlayback?.kind === 'journey';
      if (automaticJourney) {
        this.targetVideoTime = this.automaticPlayback.expectedVideoTime ?? this.config.epilogue.videoStart;
      } else {
        this.targetVideoTime = progress >= V2_PHASES.epilogueStable
          ? this.config.epilogue.blackTarget
          : lerp(this.config.epilogue.videoStart, this.config.epilogue.blackTarget, easeInOutCubic(filmProgress));
        this.seekVideo(this.targetVideoTime, { force: enteredStable });
      }
      if (progress >= 0.12) this.emitOnce('epilogue_view', {}, 'epilogue:view');
    }

    stateForChapterProgress(progress, stableProgress = V2_PHASES.categoryStable) {
      if (progress < V2_PHASES.previousExitEnd) return 'chapter-previous-exit';
      if (progress < V2_PHASES.phrase1End) return 'chapter-phrase-1';
      if (progress < V2_PHASES.phrase2Start) return 'chapter-breathing';
      if (progress < V2_PHASES.phrase2End) return 'chapter-phrase-2';
      if (progress < V2_PHASES.phrase3Start) return 'chapter-breathing';
      if (progress < V2_PHASES.phrase3End) return 'chapter-phrase-3';
      if (progress < V2_PHASES.phrase4Start) return 'chapter-breathing';
      if (progress < V2_PHASES.phrase4End) return 'chapter-phrase-4';
      if (progress < V2_PHASES.curtainCloseStart) return 'chapter-breathing';
      if (progress < V2_PHASES.titleEnterStart) return 'chapter-curtain';
      if (progress < V2_PHASES.titleEnterEnd) return 'chapter-title-enter';
      if (progress < V2_PHASES.titleExitEnd) return 'chapter-title-exit';
      if (progress < stableProgress) return 'chapter-reveal';
      return 'chapter-stable';
    }

    visualStateForChapterState(state) {
      if (state === 'chapter-previous-exit') return 'previous-exit';
      if (state.startsWith('chapter-phrase-') || state === 'chapter-breathing') return 'prelude';
      if (state === 'chapter-curtain') return 'curtain';
      if (state === 'chapter-title-enter' || state === 'chapter-title-exit') return 'title';
      if (state === 'chapter-reveal') return 'reveal';
      if (state === 'chapter-stable') return 'stable';
      return 'prelude';
    }

    phraseIndexForProgress(progress) {
      if (progress >= V2_PHASES.phrase1Start && progress < V2_PHASES.phrase1End) return 1;
      if (progress >= V2_PHASES.phrase2Start && progress < V2_PHASES.phrase2End) return 2;
      if (progress >= V2_PHASES.phrase3Start && progress < V2_PHASES.phrase3End) return 3;
      if (progress >= V2_PHASES.phrase4Start && progress < V2_PHASES.phrase4End) return 4;
      return 0;
    }

    renderPhrase(category, progress) {
      const activeIndex = this.phraseIndexForProgress(progress);
      this.phrases.forEach((phrase) => {
        const phraseIndex = finite(phrase.dataset.index, 0);
        const currentCategory = phrase.dataset.category === category;
        const active = currentCategory && phraseIndex === activeIndex;
        const past = currentCategory && (activeIndex > 0 ? phraseIndex < activeIndex : progress >= V2_PHASES.phrase4End);
        if (phrase.dataset.position !== 'center') phrase.dataset.position = 'center';
        phrase.classList.toggle('is-active', active);
        phrase.classList.toggle('is-past', past);
        phrase.setAttribute('aria-hidden', 'true');
      });
      this.writeStyle('--ld-film-phrase-progress', activeIndex > 0 ? 1 : 0);
    }

    renderTitle(category, progress) {
      const title = category ? this.titleFor(category) : '';
      if (this.title && this.title.textContent !== title) this.title.textContent = title;
      const visible = Boolean(category && progress >= V2_PHASES.curtainCloseStart && progress < V2_PHASES.portalOpenEnd);
      this.curtain?.classList.toggle('is-visible', visible);
      const titleVisible = category !== '' && progress >= V2_PHASES.titleEnterStart && progress < V2_PHASES.titleExitEnd;
      this.title?.classList.toggle('is-visible', titleVisible);
      this.signature?.classList.toggle('is-visible', titleVisible);
    }

    titleFor(category) {
      return this.visualFor(category)?.dataset.title || ({ viso: 'VISO', capelli: 'CAPELLI', corpo: 'CORPO', makeup: 'MAKE-UP' })[category] || category.toUpperCase();
    }

    visualFor(category) {
      return this.visualCategories.find((element) => element.dataset.category === category) || null;
    }

    renderVisualCategory(category, visible, interactive = false) {
      this.visualCategories.forEach((element) => {
        const active = visible && element.dataset.category === category;
        element.classList.toggle('is-active', active);
        const hidden = active ? 'false' : 'true';
        if (element.getAttribute('aria-hidden') !== hidden) element.setAttribute('aria-hidden', hidden);
        if (element.inert !== !(active && interactive)) element.inert = !(active && interactive);
      });
    }

    setAllVisualCategoriesInactive() {
      this.visualCategories.forEach((element) => {
        element.classList.remove('is-active');
        if (element.getAttribute('aria-hidden') !== 'true') element.setAttribute('aria-hidden', 'true');
        if (!element.inert) element.inert = true;
      });
    }

    openAppPreviewOnce() {
      if (this.appPreviewAutoOpened) return;
      this.appPreviewAutoOpened = true;
      document.dispatchEvent(new CustomEvent('ld:app-preview-open', { detail: { source: 'home-finale' } }));
    }

    setEpilogueActive(active) {
      if (!this.epilogue) return;
      this.epilogue.classList.toggle('is-active', active);
      if (this.staticTier || active) {
        this.epilogue.removeAttribute('inert');
        this.epilogue.setAttribute('aria-hidden', 'false');
      } else {
        this.epilogue.setAttribute('inert', '');
        this.epilogue.setAttribute('aria-hidden', 'true');
      }
      if (active) this.openAppPreviewOnce();
    }

    writeChapterStyles(progress, stableProgress = V2_PHASES.categoryStable) {
      const requestedIntensity = ({ low: 0.45, balanced: 0.72, full: 1 })[this.config.motionIntensity] || 0.72;
      const intensity = this.saveData || this.lowCapability || this.shortViewportQuery.matches
        ? Math.min(0.45, requestedIntensity)
        : requestedIntensity;
      const previousCtaExit = 1 - easeInOutCubic(segment(progress, 0, 0.018));
      const previousCertsExit = 1 - easeInOutCubic(segment(progress, 0.01, 0.032));
      const previousCopyExit = 1 - easeInOutCubic(segment(progress, 0.02, 0.046));
      const previousImageExit = 1 - easeInOutCubic(segment(progress, 0.035, V2_PHASES.previousExitEnd));
      const curtainClose = easeInOutCubic(segment(progress, V2_PHASES.curtainCloseStart, V2_PHASES.curtainClosed));
      const portalOpen = easeInOutCubic(segment(progress, V2_PHASES.portalOpenStart, V2_PHASES.portalOpenEnd));
      const curtainOpacity = curtainClose * (1 - portalOpen);
      const titleEnter = easeInOutCubic(segment(progress, V2_PHASES.titleEnterStart, V2_PHASES.titleEnterEnd));
      const titleExit = easeInOutCubic(segment(progress, V2_PHASES.titleExitStart, V2_PHASES.titleExitEnd));
      const titleDepthScale = progress < V2_PHASES.titleExitStart
        ? lerp(this.config.titleEnterScale, 1, titleEnter)
        : lerp(1, this.config.titleExitScale, titleExit);
      const titleFadeOutStart = V2_PHASES.titleExitStart +
        (V2_PHASES.titleExitEnd - V2_PHASES.titleExitStart) * 0.65;
      const titleOpacity = Math.min(
        easeInOutCubic(segment(progress, V2_PHASES.titleEnterStart, V2_PHASES.titleEnterStart + 0.045)),
        1 - easeInOutCubic(segment(progress, titleFadeOutStart, V2_PHASES.titleExitEnd)),
      );
      const titleBlur = progress < V2_PHASES.titleEnterStart
        ? 8
        : lerp(8, 0, easeInOutCubic(segment(
          progress,
          V2_PHASES.titleEnterStart,
          V2_PHASES.titleEnterStart + (V2_PHASES.titleEnterEnd - V2_PHASES.titleEnterStart) / 2,
        )));
      const revealSpan = Math.max(0.02, stableProgress - V2_PHASES.categoryRevealStart);
      const categoryReveal = easeInOutCubic(segment(progress, V2_PHASES.categoryRevealStart, stableProgress));
      const imageIn = easeInOutCubic(segment(progress, V2_PHASES.categoryRevealStart, V2_PHASES.categoryRevealStart + revealSpan * 0.42));
      const certsIn = easeInOutCubic(segment(progress, V2_PHASES.categoryRevealStart + revealSpan * 0.25, V2_PHASES.categoryRevealStart + revealSpan * 0.67));
      const copyIn = easeInOutCubic(segment(progress, V2_PHASES.categoryRevealStart + revealSpan * 0.42, V2_PHASES.categoryRevealStart + revealSpan * 0.83));
      const ctaStart = V2_PHASES.categoryRevealStart + revealSpan * 0.67;
      const ctaIn = easeInOutCubic(segment(progress, ctaStart, stableProgress));
      const showingPrevious = progress < V2_PHASES.previousExitEnd;
      const stable = progress >= stableProgress;
      const imageOpacity = showingPrevious ? previousImageExit : (stable ? 1 : imageIn);
      const certsOpacity = showingPrevious ? previousCertsExit : (stable ? 1 : certsIn);
      const copyOpacity = showingPrevious ? previousCopyExit : (stable ? 1 : copyIn);
      const ctaOpacity = showingPrevious ? previousCtaExit : (stable ? 1 : ctaIn);
      if (!this.automaticPlayback?.contractV11) {
        this.writeStyle('--ld-film-curtain-opacity', round(curtainOpacity));
        this.writeStyle('--ld-film-title-opacity', round(titleOpacity));
        this.writeStyle('--ld-v2-title-depth-scale', round(titleDepthScale));
        this.writeStyle('--ld-v2-title-blur', `${round(titleBlur)}px`);
      }
      this.writeStyle('--ld-film-title-x', '0vw');
      this.writeStyle('--ld-film-title-clip', 'inset(0)');
      this.writeStyle('--ld-film-return-clip', 'inset(0)');
      this.writeStyle('--ld-film-image-opacity', round(imageOpacity));
      this.writeStyle('--ld-film-image-scale', round(showingPrevious ? 1 : (stable ? 1 : 1.04 - 0.04 * imageIn)));
      this.writeStyle('--ld-film-image-drift', `${round(stable ? 0 : lerp(6, 0, imageIn) * intensity)}px`);
      this.writeStyle('--ld-film-certs-opacity', round(certsOpacity));
      this.writeStyle('--ld-film-content-opacity', round(copyOpacity));
      this.writeStyle('--ld-film-copy-opacity', round(copyOpacity));
      this.writeStyle('--ld-film-cta-opacity', round(ctaOpacity));
      this.writeStyle('--ld-v2-portal-radius', `${round(80 * portalOpen)}%`);
      this.writeStyle('--ld-v2-portal-inset', `${round(100 * (1 - portalOpen))}%`);
      this.writeStyle('--ld-v2-portal-opacity', round(portalOpen));
      this.writeStyle('--ld-v2-category-reveal', round(categoryReveal));
    }

    writeStyle(property, value) {
      if (this.root.style.getPropertyValue(property) !== String(value)) this.root.style.setProperty(property, value);
    }

    timelineElapsedFractionForProgress(world, progress) {
      const timeline = this.timelineMapFor(world);
      if (!timeline) return clamp(progress);
      const bounded = clamp(progress, 0, timeline.stableProgress);
      if (bounded >= timeline.stableProgress) return 1;
      const segmentRecord = timeline.segments.find((candidate) => bounded <= candidate.toProgress + CONFIG_TOLERANCE) ||
        timeline.segments[timeline.segments.length - 1];
      const local = segment(bounded, segmentRecord.fromProgress, segmentRecord.toProgress);
      return clamp((segmentRecord.startAt + segmentRecord.duration * local) / Math.max(1, timeline.totalDuration));
    }

    videoTimeForCategory(category, progress) {
      const bounded = clamp(progress);
      if (bounded >= category.stableProgress) return category.videoEnd;
      return lerp(category.videoStart, category.videoEnd, segment(bounded, 0, category.stableProgress));
    }

    setActive(world, state, localProgress, visualState) {
      const changedWorld = world !== this.activeWorld;
      this.activeWorld = world;
      this.activeState = state;
      this.activeLocalProgress = clamp(localProgress);
      if (this.root.dataset.activeWorld !== world) this.root.dataset.activeWorld = world;
      if (this.root.dataset.state !== state) this.root.dataset.state = state;
      if (this.root.dataset.visualState !== visualState) this.root.dataset.visualState = visualState;
      const phase = this.compatibilityPhase(state);
      if (this.root.dataset.activePhase !== phase) this.root.dataset.activePhase = phase;
      if (changedWorld) this.updateShortcuts(world);
      this.updateNavigation(world);
    }

    compatibilityPhase(state = this.activeState) {
      if (state === 'chapter-stable' || state.startsWith('epilogue')) return 'enter-ready';
      if (state === 'chapter-curtain' || state === 'chapter-title-enter' || state === 'chapter-title-exit' || state === 'chapter-reveal') return 'product-proof';
      return 'arrival';
    }

    updateShortcuts(world) {
      this.shortcuts?.querySelectorAll('[data-dv2-destination-world]').forEach((item) => {
        const current = item.dataset.dv2DestinationWorld === world && CATEGORY_IDS.includes(world);
        item.classList.toggle('is-current', current);
        if (current) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    updateNavigation(world = this.activeWorld) {
      if (!this.navigation) return;
      const categoryIndex = CATEGORY_IDS.indexOf(world);
      const isIntro = world === 'handoff';
      const isEpilogue = world === 'epilogue';
      const currentIndex = isEpilogue ? CATEGORY_IDS.length - 1 : Math.max(0, categoryIndex);
      const count = String(currentIndex + 1).padStart(2, '0');
      if (this.navigationCurrent && this.navigationCurrent.textContent !== count) this.navigationCurrent.textContent = count;
      const dialogSuspended = this.suspensions.has('dialog') || this.suspensions.has('assistant') ||
        Array.from(this.suspensions).some((reason) => reason.startsWith('external:'));
      const automaticBusy = Boolean(this.automaticPlayback);
      if (automaticBusy) this.navigation.setAttribute('aria-busy', 'true');
      else this.navigation.removeAttribute('aria-busy');
      const previousTarget = isEpilogue
        ? CATEGORY_IDS[CATEGORY_IDS.length - 1]
        : (categoryIndex > 0 ? CATEGORY_IDS[categoryIndex - 1] : null);
      const nextTarget = isIntro
        ? CATEGORY_IDS[0]
        : (categoryIndex >= 0 && categoryIndex < CATEGORY_IDS.length - 1
          ? CATEGORY_IDS[categoryIndex + 1]
          : (categoryIndex === CATEGORY_IDS.length - 1 ? 'epilogue' : null));
      if (this.previousButton) {
        const disabled = automaticBusy || dialogSuspended || !previousTarget;
        const label = previousTarget
          ? `Vai al capitolo precedente: ${this.titleFor(previousTarget)}`
          : 'Nessun capitolo precedente';
        if (this.previousButton.disabled !== disabled) this.previousButton.disabled = disabled;
        if (automaticBusy) this.previousButton.setAttribute('aria-busy', 'true');
        else this.previousButton.removeAttribute('aria-busy');
        if (this.previousButton.getAttribute('aria-label') !== label) this.previousButton.setAttribute('aria-label', label);
      }
      if (this.nextButton) {
        const disabled = automaticBusy || dialogSuspended || isEpilogue || !nextTarget;
        const label = nextTarget === 'epilogue'
          ? 'Vai al finale App e Community'
          : (nextTarget ? `Vai al capitolo successivo: ${this.titleFor(nextTarget)}` : 'Nessun capitolo successivo');
        if (this.nextButton.disabled !== disabled) this.nextButton.disabled = disabled;
        if (automaticBusy) this.nextButton.setAttribute('aria-busy', 'true');
        else this.nextButton.removeAttribute('aria-busy');
        if (this.nextButton.getAttribute('aria-label') !== label) this.nextButton.setAttribute('aria-label', label);
      }
    }

    navigationTarget(direction) {
      const categoryIndex = CATEGORY_IDS.indexOf(this.activeWorld);
      if (direction === 'previous') {
        if (this.activeWorld === 'epilogue') return CATEGORY_IDS[CATEGORY_IDS.length - 1];
        return categoryIndex > 0 ? CATEGORY_IDS[categoryIndex - 1] : null;
      }
      if (this.activeWorld === 'handoff') return CATEGORY_IDS[0];
      if (categoryIndex >= 0 && categoryIndex < CATEGORY_IDS.length - 1) return CATEGORY_IDS[categoryIndex + 1];
      if (categoryIndex === CATEGORY_IDS.length - 1) return 'epilogue';
      return null;
    }

    navigateChapter(direction) {
      if (this.automaticPlayback || this.transitionInProgress) return false;
      if (!['previous', 'next'].includes(direction)) return false;
      return this.requestJourneyStep(direction, 'navigation');
    }

    phraseReadingDuration(category, index) {
      const phrase = this.phrases.find((candidate) => candidate.dataset.category === category && finite(candidate.dataset.index, 0) === index);
      const wordCount = (phrase?.textContent || '').trim().split(/\s+/).filter(Boolean).length;
      return clamp(
        wordCount / AUTO_TIMINGS.phraseWordsPerSecond * 1000 + AUTO_TIMINGS.phraseReadingBuffer,
        AUTO_TIMINGS.phraseMinimum,
        AUTO_TIMINGS.phraseMaximum,
      );
    }

    timelineDefinitionsFor(world) {
      if (!CATEGORY_IDS.includes(world)) return [];
      const category = this.config.categories[CATEGORY_IDS.indexOf(world)];
      if (!category) return [];
      return [
        Object.freeze({ id: 'previous-exit', fromProgress: 0, toProgress: V2_PHASES.previousExitEnd, duration: AUTO_TIMINGS.previousExit }),
        Object.freeze({ id: 'phrase-1', fromProgress: V2_PHASES.phrase1Start, toProgress: V2_PHASES.phrase1End, duration: this.phraseReadingDuration(world, 1) }),
        Object.freeze({ id: 'phrase-breathing-1', fromProgress: V2_PHASES.phrase1End, toProgress: V2_PHASES.phrase2Start, duration: AUTO_TIMINGS.interPhraseBreathing }),
        Object.freeze({ id: 'phrase-2', fromProgress: V2_PHASES.phrase2Start, toProgress: V2_PHASES.phrase2End, duration: this.phraseReadingDuration(world, 2) }),
        Object.freeze({ id: 'phrase-breathing-2', fromProgress: V2_PHASES.phrase2End, toProgress: V2_PHASES.phrase3Start, duration: AUTO_TIMINGS.interPhraseBreathing }),
        Object.freeze({ id: 'phrase-3', fromProgress: V2_PHASES.phrase3Start, toProgress: V2_PHASES.phrase3End, duration: this.phraseReadingDuration(world, 3) }),
        Object.freeze({ id: 'phrase-breathing-3', fromProgress: V2_PHASES.phrase3End, toProgress: V2_PHASES.phrase4Start, duration: AUTO_TIMINGS.interPhraseBreathing }),
        Object.freeze({ id: 'phrase-4', fromProgress: V2_PHASES.phrase4Start, toProgress: V2_PHASES.phrase4End, duration: this.phraseReadingDuration(world, 4) }),
        Object.freeze({ id: 'breathing', fromProgress: V2_PHASES.phrase4End, toProgress: V2_PHASES.breathingEnd, duration: AUTO_TIMINGS.closingBreathing }),
        Object.freeze({ id: 'portal', fromProgress: V2_PHASES.breathingEnd, toProgress: V2_PHASES.portalOpenEnd, duration: AUTO_TIMINGS.portalReveal }),
        Object.freeze({ id: 'category-reveal', fromProgress: V2_PHASES.portalOpenEnd, toProgress: category.stableProgress, duration: AUTO_TIMINGS.categoryReveal }),
      ];
    }

    timelineMapFor(world) {
      if (!CATEGORY_IDS.includes(world)) return null;
      const category = this.config.categories[CATEGORY_IDS.indexOf(world)];
      const definitions = this.timelineDefinitionsFor(world);
      if (!category || !definitions.length) return null;
      const totalDuration = definitions.reduce((total, definition) => total + definition.duration, 0);
      let elapsed = 0;
      const segments = definitions.map((definition) => {
        const startAt = elapsed;
        const endAt = startAt + definition.duration;
        elapsed = endAt;
        return Object.freeze({
          ...definition,
          startAt,
          endAt,
          rawStart: category.stableProgress * (startAt / totalDuration),
          rawEnd: category.stableProgress * (endAt / totalDuration),
        });
      });
      return Object.freeze({
        world,
        stableProgress: category.stableProgress,
        totalDuration,
        segments: Object.freeze(segments),
      });
    }

    narrativeProgressForScroll(_world, rawProgress) {
      return clamp(rawProgress);
    }

    automaticTimelineFor(world, startY = window.scrollY) {
      if (!CATEGORY_IDS.includes(world)) return null;
      if (this.geometryDirty) this.refreshGeometry();
      const index = CATEGORY_IDS.indexOf(world);
      const geometry = this.geometry?.categories[index];
      const category = this.config.categories[index];
      const timeline = this.timelineMapFor(world);
      if (!geometry || !category || !timeline) return null;

      const yForRawProgress = (progress) => scrollTargetAtOrAfter(geometry, progress);
      let previousY = startY;
      const segments = timeline.segments.map((definition, segmentIndex) => {
        const fromY = segmentIndex === 0 ? previousY : yForRawProgress(definition.rawStart);
        const toY = yForRawProgress(definition.rawEnd);
        previousY = toY;
        return Object.freeze({
          id: definition.id,
          fromProgress: definition.fromProgress,
          toProgress: definition.toProgress,
          fromY,
          toY,
          duration: definition.duration,
          startAt: definition.startAt,
          endAt: definition.endAt,
        });
      });
      return Object.freeze({
        world,
        totalDuration: timeline.totalDuration,
        targetY: yForRawProgress(category.stableProgress),
        segments: Object.freeze(segments),
      });
    }

    playAutomaticJourney(world) {
      const targetWorld = this.navigationTarget('next');
      if (world !== targetWorld) return false;
      return this.requestJourneyStep('next', 'navigation');
    }

    playPreviousToStable(world) {
      const targetWorld = this.navigationTarget('previous');
      if (world !== targetWorld) return false;
      return this.requestJourneyStep('previous', 'navigation');
    }

    beginAutomaticPlayback(playback) {
      if (this.automaticPlayback || this.transitionInProgress || this.destroyed || this.staticTier || this.isSuspended) return false;
      const playingState = this.playingJourneyStateForWorld(playback.targetWorld);
      if (!playingState) return false;
      this.introConsumed = true;
      this.completeIntroTitle('chapter_trigger');
      this.cancelIntro();
      this.pauseIntroSequence();
      this.manualVideoAnchor = null;
      this.setIntroState(INTRO_STATES.scroll);
      this.setIntroControls(false);
      this.automaticPlayback = playback;
      this.setJourneyState(playingState);
      this.root.classList.add('is-automatic-playback');
      this.root.classList.toggle('is-automatic-return', playback.kind === 'return');
      this.root.dataset.automaticPlayback = playback.kind;
      this.root.dataset.automaticTarget = playback.targetWorld;
      this.root.dataset.automaticDuration = String(Math.round(playback.totalDuration));
      this.root.dataset.automaticMedia = 'pending';
      document.documentElement.classList.add('ld-home-film-auto-playback');
      this.updateNavigation();
      this.setSkipVisible(false);
      this.announce(`Percorso verso ${this.titleFor(playback.targetWorld)}`);
      this.startAutomaticMedia(playback);
      return true;
    }

    startAutomaticMedia(playback) {
      if (this.automaticPlayback !== playback || !['journey', 'return'].includes(playback.kind)) return;
      if (!this.video || this.mediaFallback) {
        this.useVideoFallback('chapter_media_unavailable');
        return;
      }
      // Metadata readiness does not guarantee the requested frame can play.
      // Bound both the marker seek and a play promise that never settles.
      clearTimeout(this.automaticMediaStartTimer);
      const checkMediaStart = () => {
        if (this.destroyed || this.automaticPlayback !== playback) return;
        if (this.isSuspended || document.hidden) {
          this.automaticMediaStartTimer = window.setTimeout(checkMediaStart, 1000);
          return;
        }
        this.automaticMediaStartTimer = 0;
        this.useVideoFallback('chapter_media_start_timeout');
      };
      this.automaticMediaStartTimer = window.setTimeout(checkMediaStart, 8000);
      if (!this.mediaReady) {
        playback.mediaMode = 'waiting';
        this.root.dataset.automaticMedia = 'waiting';
        this.targetVideoTime = playback.mediaStart;
        return;
      }

      const token = ++this.automaticMediaToken;
      this.video.pause();
      this.resetVideoPlaybackRate();
      this.targetVideoTime = playback.mediaStart;
      playback.expectedVideoTime = playback.mediaStart;
      playback.startedAt = null;
      playback.elapsed = 0;
      this.journeyLastMediaTime = null;
      this.journeyLastProgressAt = performance.now();

      const startFrames = () => {
        if (token !== this.automaticMediaToken || this.automaticPlayback !== playback || this.destroyed || this.isSuspended) return;
        clearTimeout(this.automaticMediaStartTimer);
        this.automaticMediaStartTimer = 0;
        if (!this.automaticFrame) this.automaticFrame = requestAnimationFrame((timestamp) => this.runAutomaticPlayback(timestamp));
      };

      const startNative = () => {
        if (token !== this.automaticMediaToken || this.automaticPlayback !== playback) return;
        this.setChapterPlaybackRate();
        playback.mediaMode = 'native';
        this.root.dataset.automaticMedia = 'native';
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(startFrames).catch(() => {
            if (token !== this.automaticMediaToken || this.automaticPlayback !== playback) return;
            this.useVideoFallback('chapter_play_rejected');
          });
        } else {
          startFrames();
        }
      };

      const startReverse = () => {
        if (token !== this.automaticMediaToken || this.automaticPlayback !== playback) return;
        playback.mediaMode = 'reverse-seek';
        this.root.dataset.automaticMedia = 'reverse-seek';
        startFrames();
      };

      const startAfterMarkerSeek = () => {
        if (playback.kind === 'journey') startNative();
        else startReverse();
      };

      if (Math.abs(this.video.currentTime - playback.mediaStart) > FRAME_DURATION) {
        this.seekVideo(playback.mediaStart, { force: true });
        if (this.video.seeking) {
          this.video.addEventListener('seeked', startAfterMarkerSeek, { once: true, signal: this.signal });
          return;
        }
      }
      startAfterMarkerSeek();
    }

    automaticVideoTime(playback, elapsed) {
      const progress = clamp(elapsed / Math.max(1, playback.totalDuration));
      return lerp(playback.mediaStart, playback.mediaEnd, progress);
    }

    runAutomaticPlayback(timestamp) {
      this.automaticFrame = 0;
      const playback = this.automaticPlayback;
      if (!playback || this.destroyed || this.isSuspended) return;
      if (playback.mediaMode === 'waiting' || playback.mediaMode === 'pending') return;
      const renderWorld = safeCategory(playback.renderWorld);
      const renderIndex = renderWorld ? CATEGORY_IDS.indexOf(renderWorld) : -1;
      const renderCategory = renderIndex >= 0 ? this.config.categories[renderIndex] : null;
      if (!renderCategory) {
        this.useVideoFallback('chapter_render_world_missing');
        return;
      }

      if (playback.kind === 'journey') {
        const mediaTime = Number.isFinite(this.automaticPresentedMediaTime)
          ? this.automaticPresentedMediaTime
          : this.video.currentTime;
        if (this.journeyLastMediaTime === null || mediaTime > this.journeyLastMediaTime + 0.005) {
          this.journeyLastMediaTime = mediaTime;
          this.journeyLastProgressAt = performance.now();
        } else if (performance.now() - this.journeyLastProgressAt >= INTRO_STALL_TIMEOUT) {
          this.useVideoFallback('chapter_playback_stalled');
          return;
        }
        const fraction = clamp((mediaTime - playback.mediaStart) / Math.max(FRAME_DURATION, playback.mediaEnd - playback.mediaStart));
        playback.expectedVideoTime = mediaTime;
        this.targetVideoTime = mediaTime;
        const progress = renderCategory.stableProgress * fraction;
        this.renderForward(renderIndex, progress);
        if (mediaTime >= playback.mediaEnd - FRAME_DURATION * 0.75) {
          this.finishAutomaticPlayback();
          return;
        }
      } else {
        if (playback.startedAt === null) playback.startedAt = timestamp;
        const elapsed = Math.max(0, timestamp - playback.startedAt);
        playback.elapsed = elapsed;
        const fraction = clamp(elapsed / Math.max(1, playback.totalDuration));
        const targetTime = this.automaticVideoTime(playback, elapsed);
        playback.expectedVideoTime = targetTime;
        this.targetVideoTime = targetTime;
        this.seekVideo(targetTime);
        const progress = lerp(renderCategory.stableProgress, 0, fraction);
        this.renderForward(renderIndex, progress);
        if (fraction >= 1) {
          this.finishAutomaticPlayback();
          return;
        }
      }

      this.automaticFrame = requestAnimationFrame((nextTimestamp) => this.runAutomaticPlayback(nextTimestamp));
    }

    monitorJourneyMarker() {
      const playback = this.automaticPlayback;
      if (!playback || playback.kind !== 'journey' || playback.mediaMode !== 'native' || !this.video) return;
      if (this.video.currentTime >= playback.mediaEnd - FRAME_DURATION * 0.75) this.finishAutomaticPlayback();
    }

    setChapterPlaybackRate() {
      if (!this.video) return;
      try {
        if (this.video.defaultPlaybackRate !== CHAPTER_PLAYBACK_RATE) this.video.defaultPlaybackRate = CHAPTER_PLAYBACK_RATE;
        if (Math.abs(this.video.playbackRate - CHAPTER_PLAYBACK_RATE) > 0.001) this.video.playbackRate = CHAPTER_PLAYBACK_RATE;
      } catch (_error) {}
    }

    resetVideoPlaybackRate() {
      if (!this.video) return;
      try {
        if (this.video.defaultPlaybackRate !== 1) this.video.defaultPlaybackRate = 1;
        if (this.video.playbackRate !== 1) this.video.playbackRate = 1;
      } catch (_error) {}
    }

    trackAutomaticPresentedFrames(playback) {
      this.stopAutomaticPresentedFrameTracking();
      this.automaticPresentedMediaTime = null;
      if (!this.video?.requestVideoFrameCallback) return;
      this.automaticPresentedMediaTime = playback.mediaStart;
      const record = (_now, metadata) => {
        this.automaticPresentedFrameCallback = 0;
        if (this.automaticPlayback !== playback || playback.mediaMode !== 'native' || this.destroyed) return;
        if (Number.isFinite(metadata?.mediaTime)) this.automaticPresentedMediaTime = metadata.mediaTime;
        this.automaticPresentedFrameCallback = this.video.requestVideoFrameCallback(record);
      };
      this.automaticPresentedFrameCallback = this.video.requestVideoFrameCallback(record);
    }

    stopAutomaticPresentedFrameTracking() {
      if (this.automaticPresentedFrameCallback && this.video?.cancelVideoFrameCallback) {
        this.video.cancelVideoFrameCallback(this.automaticPresentedFrameCallback);
      }
      this.automaticPresentedFrameCallback = 0;
    }

    stopAutomaticMedia(playback) {
      this.stopAutomaticPresentedFrameTracking();
      if (playback?.kind === 'journey') this.video?.pause();
      this.resetVideoPlaybackRate();
      this.clearAutomaticSeekQueue();
    }

    queueAutomaticSeek(target) {
      const playback = this.automaticPlayback;
      if (!playback || playback.kind !== 'journey' || playback.mediaMode !== 'queued-seek' ||
        !this.video || !this.mediaReady || this.mediaFallback || this.isSuspended) return;
      this.automaticSeekPending = clamp(target, playback.mediaStart, playback.mediaEnd);
      if (this.automaticSeekInFlight || this.automaticSeekFrameCallback || this.automaticSeekTimer) return;
      this.flushAutomaticSeekQueue();
    }

    flushAutomaticSeekQueue() {
      this.automaticSeekFrameCallback = 0;
      const playback = this.automaticPlayback;
      if (!playback || playback.kind !== 'journey' || playback.mediaMode !== 'queued-seek' ||
        !this.video || !Number.isFinite(this.automaticSeekPending) || this.isSuspended) return;
      const remainingDelay = AUTOMATIC_SEEK_MIN_INTERVAL - (performance.now() - this.automaticSeekLastAt);
      if (remainingDelay > 1) {
        this.automaticSeekTimer = window.setTimeout(() => {
          this.automaticSeekTimer = 0;
          this.flushAutomaticSeekQueue();
        }, remainingDelay);
        return;
      }
      const target = this.automaticSeekPending;
      this.automaticSeekPending = null;
      if (Math.abs(target - this.video.currentTime) < FRAME_DURATION * 0.5) return;
      try {
        this.automaticSeekInFlight = true;
        this.automaticSeekLastAt = performance.now();
        this.video.currentTime = target;
      } catch (_error) {
        this.automaticSeekInFlight = false;
        playback.mediaMode = 'unavailable';
        this.root.dataset.automaticMedia = 'unavailable';
      }
    }

    scheduleAutomaticSeekFlush() {
      if (this.automaticSeekFrameCallback || this.automaticSeekTimer || !Number.isFinite(this.automaticSeekPending)) return;
      this.automaticSeekFrameCallback = requestAnimationFrame(() => this.flushAutomaticSeekQueue());
    }

    clearAutomaticSeekQueue() {
      if (this.automaticSeekFrameCallback) cancelAnimationFrame(this.automaticSeekFrameCallback);
      this.automaticSeekFrameCallback = 0;
      if (this.automaticSeekTimer) window.clearTimeout(this.automaticSeekTimer);
      this.automaticSeekTimer = 0;
      this.automaticSeekPending = null;
      this.automaticSeekInFlight = false;
      this.automaticSeekLastAt = 0;
    }

    captureManualVideoAnchor(playback) {
      if (playback?.kind !== 'journey' || !this.video) return;
      const categoryId = safeCategory(this.activeWorld);
      const category = categoryId ? this.config.categories[CATEGORY_IDS.indexOf(categoryId)] : null;
      if (!category || this.activeLocalProgress >= category.stableProgress) return;
      const presentedTime = Number.isFinite(this.automaticPresentedMediaTime)
        ? this.automaticPresentedMediaTime
        : this.video.currentTime;
      const time = clamp(presentedTime, category.videoStart, category.videoEnd);
      this.targetVideoTime = time;
      this.manualVideoAnchor = Object.freeze({
        categoryId,
        progress: clamp(this.activeLocalProgress, 0, category.stableProgress),
        time,
      });
    }

    finishAutomaticPlayback() {
      const playback = this.automaticPlayback;
      if (!playback) return false;
      if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
      this.automaticFrame = 0;
      this.stopAutomaticMedia(playback);
      const targetY = Number.isFinite(playback.targetY) ? playback.targetY : this.targetForWorld(playback.targetWorld);
      if (Number.isFinite(targetY)) this.scrollToJourneyAnchor(targetY);
      this.finalizeStableWorld(playback.targetWorld);
      this.setJourneyState(this.stableJourneyStateForWorld(playback.targetWorld));
      this.clearAutomaticPlaybackState();
      this.setSkipVisible(false);
      this.updateNavigation();
      this.emit('automatic_chapter_complete', {
        category: playback.targetWorld,
        method: playback.kind,
        world_id: playback.targetWorld,
      });
      this.announce(`Capitolo ${this.titleFor(playback.targetWorld)} pronto`);
      return true;
    }

    finalizeStableWorld(world, { deferVideoSeek = false } = {}) {
      this.manualVideoAnchor = null;
      if (world === 'epilogue') {
        this.lastSegmentIndex = CATEGORY_IDS.length;
        this.targetVideoTime = this.config.epilogue.blackTarget;
        this.setAllVisualCategoriesInactive();
        this.setEpilogueActive(true);
        this.setActive('epilogue', 'epilogue-stable', 1, 'epilogue');
      } else {
        const index = CATEGORY_IDS.indexOf(world);
        const category = this.config.categories[index];
        if (!category) return;
        this.lastSegmentIndex = index;
        this.activeCategoryIndex = index;
        this.targetVideoTime = category.videoEnd;
        this.setEpilogueActive(false);
        this.setActive(world, 'chapter-stable', category.stableProgress, 'stable');
        this.writeChapterStyles(category.stableProgress, category.stableProgress);
        this.renderPhrase(world, 1);
        this.renderTitle(world, 0);
        this.renderVisualCategory(world, true, true);
        const stableState = this.stableJourneyStateForWorld(world);
        if (stableState) this.setJourneyState(stableState);
        const stableTarget = this.targetForWorld(world);
        if (Number.isFinite(stableTarget)) this.journeyAnchorY = stableTarget;
      }
      if (deferVideoSeek && this.video?.seeking) {
        const stableTime = this.targetVideoTime;
        this.video.addEventListener('seeked', () => {
          requestAnimationFrame(() => {
            if (!this.destroyed) this.seekVideo(stableTime, { force: true });
          });
        }, { once: true, signal: this.signal });
      } else {
        this.seekVideo(this.targetVideoTime, { force: true });
      }
    }

    clearAutomaticPlaybackState() {
      clearTimeout(this.automaticMediaStartTimer);
      this.automaticMediaStartTimer = 0;
      this.automaticMediaToken += 1;
      this.clearAutomaticSeekQueue();
      this.stopAutomaticPresentedFrameTracking();
      this.automaticPresentedMediaTime = null;
      this.automaticPlayback = null;
      this.transitionInProgress = false;
      this.root.dataset.transitionInProgress = 'false';
      this.journeyLastMediaTime = null;
      this.journeyLastProgressAt = 0;
      this.root.classList.remove('is-automatic-playback', 'is-automatic-return');
      delete this.root.dataset.automaticPlayback;
      delete this.root.dataset.automaticTarget;
      delete this.root.dataset.automaticDuration;
      delete this.root.dataset.automaticMedia;
      if (![...CONNECTED].some((controller) => controller !== this && controller.automaticPlayback)) {
        document.documentElement.classList.remove('ld-home-film-auto-playback');
      }
    }

    cancelAutomaticPlayback(reason = 'external_cancel', { silent = false } = {}) {
      const playback = this.automaticPlayback;
      if (!playback) return false;
      if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
      this.automaticFrame = 0;
      this.stopAutomaticMedia(playback);
      const sourceWorld = safeCategory(playback.sourceWorld);
      this.clearAutomaticPlaybackState();
      if (sourceWorld) {
        const sourceTarget = this.targetForWorld(sourceWorld);
        if (Number.isFinite(sourceTarget)) this.scrollToJourneyAnchor(sourceTarget);
        this.finalizeStableWorld(sourceWorld);
      } else {
        this.setJourneyState(JOURNEY_STATES.introReady);
        this.journeyAnchorY = this.geometry?.intro?.start ?? window.scrollY;
        this.targetVideoTime = this.config.intro.holdTime;
        this.seekVideo(this.targetVideoTime, { force: true });
        this.setActive('handoff', INTRO_STATES.hold, 0, '');
        this.revealIntroControls();
      }
      this.updateNavigation();
      if (!silent) {
        this.emit('automatic_chapter_cancel', {
          category: safeCategory(playback.targetWorld) || '',
          reason,
          world_id: playback.targetWorld,
        });
      }
      return true;
    }

    onAutomaticInput(event) {
      if (!this.automaticPlayback) return;
      if (event?.cancelable && ['wheel', 'touchmove'].includes(event.type)) event.preventDefault();
    }

    advanceIntroOnce() {
      this.cancelAutomaticPlayback('intro_advance', { silent: true });
      this.completeIntroTitle('cta');
      this.cancelIntro();
      this.pauseIntroSequence();
      this.introConsumed = true;
      this.video?.pause();
      if (this.staticTier || this.reducedMotionQuery.matches) {
        const first = this.semanticCategories.find((section) => section.dataset.category === CATEGORY_IDS[0]);
        first?.scrollIntoView({ block: 'start', behavior: 'instant' });
        return true;
      }
      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return false;
      const target = scrollTargetAtOrAfter(this.geometry.intro, 0.98);
      this.targetVideoTime = this.config.intro.holdTime;
      this.seekVideo(this.targetVideoTime, { force: true });
      this.setIntroState(INTRO_STATES.scroll);
      this.setIntroControls(false);
      this.previousScrollY = target;
      this.journeyAnchorY = target;
      this.setJourneyState(JOURNEY_STATES.introReady);
      this.scrollToJourneyAnchor(target);
      this.schedule();
      this.emit('intro_advance', { method: 'cta', destination: 'first_video_frame' });
      return true;
    }

    jumpToStableWorld(world) {
      if (!PUBLIC_WORLDS.includes(world)) return false;
      this.cancelAutomaticPlayback('stable_jump', { silent: true });
      this.manualVideoAnchor = null;
      this.introConsumed = true;
      this.completeIntroTitle('navigation');
      this.cancelIntro();
      this.pauseIntroSequence();
      this.video?.pause();

      if (this.staticTier) {
        const target = world === 'epilogue'
          ? this.epilogue
          : this.semanticCategories.find((section) => section.dataset.category === world);
        if (!target) return false;
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
        const stableProgress = world === 'epilogue'
          ? 1
          : this.config.categories[CATEGORY_IDS.indexOf(world)]?.stableProgress || V2_PHASES.categoryStable;
        this.setActive(world, world === 'epilogue' ? 'epilogue-stable' : 'chapter-stable', stableProgress, world === 'epilogue' ? 'epilogue' : 'stable');
        this.announce(world === 'epilogue' ? 'Finale App e Community pronto' : `Capitolo ${this.titleFor(world)} pronto`);
        return true;
      }

      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return false;
      let target;
      if (world === 'epilogue') {
        target = this.epilogue ? Math.max(0, this.epilogue.getBoundingClientRect().top + window.scrollY - this.headerHeight()) : this.geometry.scrollEnd;
        this.previousScrollY = target;
        this.lastSegmentIndex = CATEGORY_IDS.length;
        this.targetVideoTime = this.config.epilogue.blackTarget;
        this.setAllVisualCategoriesInactive();
        this.setEpilogueActive(true);
        this.setActive('epilogue', 'epilogue-stable', 1, 'epilogue');
        this.announce('Finale App e Community pronto');
      } else {
        const index = CATEGORY_IDS.indexOf(world);
        const category = this.config.categories[index];
        const geometry = this.geometry.categories[index];
        target = scrollTargetAtOrAfter(geometry, category.stableProgress);
        this.previousScrollY = target;
        this.lastSegmentIndex = index;
        this.activeCategoryIndex = index;
        this.targetVideoTime = category.videoEnd;
        this.setEpilogueActive(false);
        this.setActive(world, 'chapter-stable', category.stableProgress, 'stable');
        this.writeChapterStyles(category.stableProgress, category.stableProgress);
        this.renderPhrase(world, 1);
        this.renderTitle(world, 0);
        this.renderVisualCategory(world, true, true);
        const stableState = this.stableJourneyStateForWorld(world);
        if (stableState) this.setJourneyState(stableState);
        this.journeyAnchorY = target;
        this.announce(`Capitolo ${this.titleFor(world)} pronto`);
      }
      this.setSkipVisible(false);
      this.seekVideo(this.targetVideoTime, { force: true });
      this.scrollToJourneyAnchor(target);
      this.schedule();
      return true;
    }

    setSkipVisible(visible) {
      if (!this.skip) return;
      const automaticTarget = this.automaticPlayback?.targetWorld || '';
      const category = safeCategory(automaticTarget) || safeCategory(this.activeWorld);
      const epilogueTarget = automaticTarget === 'epilogue';
      const shouldShow = Boolean(visible && (category || epilogueTarget) && !this.staticTier);
      this.skip.hidden = !shouldShow;
      this.skip.classList.toggle('is-visible', shouldShow);
      const destination = epilogueTarget ? 'Finale' : (category ? this.titleFor(category) : '');
      const label = destination ? `Salta al capitolo · ${destination}` : 'Salta al capitolo';
      if (this.skipDestination) this.skipDestination.textContent = shouldShow ? ` ${destination}` : '';
      this.skipLabels.forEach((element) => {
        if (element.textContent !== label) element.textContent = label;
      });
      this.skip.setAttribute('aria-label', label);
    }

    setIntroControls(visible) {
      if (this.introActions) {
        this.introActions.classList.toggle('is-visible', visible);
        this.introActions.setAttribute('aria-hidden', visible ? 'false' : 'true');
        if (visible) this.introActions.removeAttribute('inert');
        else this.introActions.setAttribute('inert', '');
      }
      this.scrollCue?.classList.toggle('is-visible', visible);
      this.root.classList.toggle('has-intro-controls', visible);
    }

    revealIntroControls() {
      if (this.introControlsRevealed) return;
      this.introControlsRevealed = true;
      this.setIntroControls(true);
    }

    loadMediaForViewport({ preserveTime = true } = {}) {
      if (!this.video || this.staticTier || this.destroyed) return;
      const mobile = this.mobileQuery.matches;
      const desktopSource = (this.video.dataset.srcDesktop || '').trim();
      const mobileSource = (this.video.dataset.srcMobile || '').trim();
      const source = mobile ? (mobileSource || desktopSource) : (desktopSource || mobileSource);
      const sourceKind = mobile && mobileSource ? 'mobile' : 'desktop';
      this.updatePosterForViewport();
      this.root.dataset.mediaViewport = mobile ? 'mobile' : 'desktop';
      if (!source) {
        this.useVideoFallback('source_missing');
        this.emitOnce('home_asset_missing', { asset_type: 'video', reason: 'video_source_missing' }, 'missing:video-source');
        return;
      }
      if (source === this.activeSource && this.video.getAttribute('src') === source) {
        this.activeSourceKind = sourceKind;
        return;
      }

      const duration = Number.isFinite(this.video.duration) && this.video.duration > 0
        ? this.video.duration
        : VIDEO_DURATION;
      const preservedTime = this.activeSource ? this.video.currentTime : this.targetVideoTime;
      this.mediaSwapNormalized = preserveTime ? clamp((preservedTime || 0) / duration) : 0;
      this.resumeIntroAfterSwap = this.introPlaying;
      this.cancelIntro();
      this.video.pause();
      this.resetVideoPlaybackRate();
      this.activeSource = source;
      this.activeSourceKind = sourceKind;
      this.mediaReady = false;
      this.mediaFallback = false;
      this.root.classList.remove('is-video-fallback', 'is-video-ready', 'has-video-frame', 'is-video-seeking');
      this.video.src = source;
      this.video.preload = 'metadata';
      this.video.load();
    }

    updatePosterForViewport() {
      if (!this.video) return;
      // The responsive <picture> is the sole poster owner. Keeping a poster on
      // the video duplicates the hero request (and can fetch both viewport
      // variants) before the first decoded video frame is ready.
      this.video.removeAttribute('poster');
    }

    unloadMedia() {
      if (!this.video) return;
      this.cancelIntro();
      this.video.pause();
      this.resetVideoPlaybackRate();
      this.video.removeAttribute('src');
      this.video.load();
      this.activeSource = '';
      this.activeSourceKind = '';
      this.mediaReady = false;
      this.root.classList.remove('is-video-ready', 'has-video-frame', 'is-video-seeking');
    }

    onLoadedMetadata() {
      if (!this.video || this.staticTier || !this.video.getAttribute('src')) return;
      const duration = this.video.duration;
      if (!Number.isFinite(duration) || duration <= 0 || Math.abs(duration - VIDEO_DURATION) > VIDEO_DURATION_TOLERANCE) {
        console.warn('[LDHomeFilm] video_duration_mismatch; poster fallback is active.');
        this.useVideoFallback('video_duration_mismatch');
        return;
      }
      this.mediaReady = true;
      this.root.classList.add('is-video-ready');
      if (Number.isFinite(this.mediaSwapNormalized) && Number.isFinite(duration) && duration > 0) {
        const target = clamp(this.mediaSwapNormalized, 0, 1) * duration;
        this.mediaSwapNormalized = null;
        this.seekVideo(target, { force: true });
      }
      if (!this.introConsumed && this.isAtIntroStart()) {
        this.prepareStaticHero();
      } else if (this.resumeIntroAfterSwap) {
        this.prepareStaticHero();
      } else {
        this.seekVideo(this.targetVideoTime, { force: true });
      }
      this.resumeIntroAfterSwap = false;
      if (this.automaticPlayback?.mediaMode === 'waiting') this.startAutomaticMedia(this.automaticPlayback);
    }

    startIntro({ preserveCurrentTime = false } = {}) {
      if (!this.video || !this.mediaReady || this.introConsumed || this.staticTier || this.isSuspended) return;
      if (!this.introSequenceCompleted) {
        this.beginIntroSequence();
        return;
      }
      this.introPlaying = true;
      this.setIntroState(INTRO_STATES.autoplay);
      if (!preserveCurrentTime && this.video.currentTime > 0.01) {
        try { this.video.currentTime = 0; } catch (_error) {}
      }
      this.introLastVideoTime = this.video.currentTime;
      this.introLastProgressAt = performance.now();
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (this.introPlaying && !this.introConsumed) this.useVideoFallback('autoplay_rejected');
        });
      }
      this.monitorIntro();
    }

    monitorIntro() {
      if (!this.introPlaying || !this.video || this.destroyed) return;
      if (this.video.currentTime > this.introLastVideoTime + 0.01) {
        this.introLastVideoTime = this.video.currentTime;
        this.introLastProgressAt = performance.now();
      } else if (performance.now() - this.introLastProgressAt >= INTRO_STALL_TIMEOUT) {
        this.useVideoFallback('autoplay_stalled');
        return;
      }
      if (this.video.currentTime >= this.config.intro.ctaTime) this.revealIntroControls();
      if (this.video.currentTime >= this.config.intro.holdTime) {
        this.video.pause();
        this.introPlaying = false;
        this.introConsumed = true;
        this.resumeIntroOnUnsuspend = false;
        this.revealIntroControls();
        this.setIntroState(INTRO_STATES.hold);
        this.targetVideoTime = this.config.intro.holdTime;
        this.seekVideo(this.targetVideoTime, { force: true });
        this.emitOnce('intro_complete', { method: 'autoplay' }, 'intro:complete');
        if (this.introFrame) cancelAnimationFrame(this.introFrame);
        this.introFrame = 0;
        return;
      }
      if (!this.introFrame) {
        this.introFrame = requestAnimationFrame(() => {
          this.introFrame = 0;
          this.monitorIntro();
        });
      }
    }

    cancelIntro() {
      this.introPlaying = false;
      this.introLastVideoTime = 0;
      this.introLastProgressAt = 0;
      if (this.introFrame) cancelAnimationFrame(this.introFrame);
      this.introFrame = 0;
    }

    useVideoFallback(reason) {
      if (this.destroyed || this.mediaFallback) return;
      const failedPlayback = this.automaticPlayback;
      this.mediaFallback = true;
      this.cancelIntro();
      this.completeIntroTitle('fallback');
      this.pauseIntroSequence();
      this.video?.pause();
      this.root.classList.add('is-video-fallback');
      this.introConsumed = true;
      this.resumeIntroOnUnsuspend = false;
      if (this.activeWorld === 'handoff' || this.isAtIntroStart()) {
        this.revealIntroControls();
        this.setIntroState(INTRO_STATES.hold);
      } else {
        this.setIntroControls(false);
      }
      this.emitOnce('home_video_fallback', { reason }, `video-fallback:${reason}`);
      if (failedPlayback) {
        const targetWorld = safeCategory(failedPlayback.targetWorld);
        if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
        this.automaticFrame = 0;
        this.stopAutomaticMedia(failedPlayback);
        this.clearAutomaticPlaybackState();
        if (targetWorld) {
          const targetY = this.targetForWorld(targetWorld);
          if (Number.isFinite(targetY)) this.scrollToJourneyAnchor(targetY);
          this.finalizeStableWorld(targetWorld);
        }
      }
      this.schedule();
    }

    seekVideo(target, { force = false } = {}) {
      if (!this.video || !this.mediaReady || this.staticTier || this.mediaFallback || this.isSuspended) return;
      const duration = Number.isFinite(this.video.duration) && this.video.duration > 0 ? this.video.duration : VIDEO_DURATION;
      const bounded = clamp(finite(target, 0), 0, Math.max(0, duration - 0.01));
      if (!force && Math.abs(bounded - this.video.currentTime) < SEEK_EPSILON) return;
      try {
        this.video.currentTime = bounded;
      } catch (_error) {
        this.useVideoFallback('seek_error');
      }
    }

    onSeeked() {
      this.root.classList.remove('is-video-seeking');
      if (this.automaticPlayback?.mediaMode === 'queued-seek') {
        this.automaticSeekInFlight = false;
        this.scheduleAutomaticSeekFlush();
      }
    }

    onRootClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const introAdvance = target.closest('[data-ld-intro-advance]');
      if (introAdvance && this.root.contains(introAdvance)) {
        event.preventDefault();
        this.advanceIntroOnce();
        return;
      }
      const previous = target.closest('[data-ld-chapter-previous]');
      if (previous && this.root.contains(previous)) {
        event.preventDefault();
        this.navigateChapter('previous');
        return;
      }
      const next = target.closest('[data-ld-chapter-next]');
      if (next && this.root.contains(next)) {
        event.preventDefault();
        this.navigateChapter('next');
        return;
      }
      const skip = target.closest('[data-ld-skip]');
      if (skip && this.root.contains(skip)) {
        event.preventDefault();
        this.skipToStable();
        return;
      }
      const collectionChooser = target.closest('[data-ld-collection-chooser-trigger]');
      if (collectionChooser && this.root.contains(collectionChooser)) {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('ld:collection-chooser:open', { detail: { trigger: collectionChooser } }));
        this.emit('home_explore_collections', { destination: 'collection_chooser', method: 'dialog' });
        return;
      }
      const explore = target.closest('[data-ld-explore]');
      if (explore && this.root.contains(explore)) {
        this.emit('home_explore_collections', { destination: safeDestination(explore.href), method: 'navigation' });
        return;
      }
      const categoryLink = target.closest('[data-ld-category-cta]');
      if (categoryLink && this.root.contains(categoryLink)) {
        const category = safeCategory(categoryLink.dataset.category || categoryLink.dataset.dv2DestinationWorld);
        if (category) this.emit('category_cta_click', { category, destination: safeDestination(categoryLink.href) });
      }
      const cert = target.closest('[data-ld-cert-open]');
      if (cert && this.root.contains(cert)) {
        this.cancelAutomaticPlayback('dialog_open');
        this.hideTooltip(cert);
        const category = safeCategory(cert.dataset.ldCertCategory || cert.closest('[data-category]')?.dataset.category);
        const certificationId = String(cert.dataset.ldCertId || '').replace(/[^a-z0-9_-]/gi, '').slice(0, 64);
        this.emit('cert_open', { category: category || '', certification_id: certificationId });
        this.later(() => this.syncDialogSuspension(), 0);
      }
      const ai = target.closest('[data-ld-ai-trigger], [data-dv2-ai-open]');
      if (ai && this.root.contains(ai)) {
        const source = String(ai.dataset.ldAiSource || 'home').replace(/[^a-z0-9_-]/gi, '').slice(0, 48) || 'home';
        this.emit('ai_open', { source });
      }
    }

    onDocumentRouteClick(event) {
      const link = event.target instanceof Element ? event.target.closest('a[data-dv2-transition]') : null;
      if (!link || !this.root.contains(link)) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      try {
        const url = new URL(link.href, window.location.href);
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) this.suspend('route');
      } catch (_error) {}
    }

    skipToStable() {
      const automaticTarget = this.automaticPlayback?.targetWorld;
      if (automaticTarget && PUBLIC_WORLDS.includes(automaticTarget)) {
        const fromState = this.activeState;
        this.emit('skip_click', {
          category: safeCategory(automaticTarget) || '',
          from_state: fromState,
          world_id: automaticTarget,
        });
        this.cancelAutomaticPlayback('skip', { silent: true });
        return this.jumpToStableWorld(automaticTarget);
      }
      const category = safeCategory(this.activeWorld);
      const index = category ? CATEGORY_IDS.indexOf(category) : -1;
      const geometry = index >= 0 ? this.geometry?.categories[index] : null;
      const config = index >= 0 ? this.config.categories[index] : null;
      if (!geometry || !config || this.staticTier) return false;
      const fromState = this.activeState;
      const target = scrollTargetAtOrAfter(geometry, config.stableProgress);
      this.pendingStableAnnouncement = category;
      this.emit('skip_click', { category, from_state: fromState });
      this.previousScrollY = target;
      this.lastSegmentIndex = index;
      this.setActive(category, 'chapter-stable', config.stableProgress, 'stable');
      this.writeChapterStyles(config.stableProgress, config.stableProgress);
      this.renderPhrase(category, 1);
      this.renderTitle(category, 0);
      this.renderVisualCategory(category, true, true);
      this.setSkipVisible(false);
      this.targetVideoTime = config.videoEnd;
      this.seekVideo(this.targetVideoTime, { force: true });
      window.scrollTo({ top: target, behavior: 'instant' });
      this.schedule();
      return true;
    }

    tooltipFor(trigger) {
      const ids = (trigger.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      return ids.map((id) => document.getElementById(id)).find((element) => element?.getAttribute('role') === 'tooltip') || null;
    }

    scheduleTooltip(trigger) {
      if (!trigger || this.isSuspended) return;
      this.clearTooltipTimer(trigger);
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        this.tooltipTimers.delete(trigger);
        if (!trigger.isConnected || this.isSuspended) return;
        const tooltip = this.tooltipFor(trigger);
        tooltip?.classList.add('is-visible');
        trigger.classList.add('is-tooltip-visible');
        trigger.closest('[data-ld-cert-item]')?.classList.add('is-tooltip-visible');
      }, 160);
      this.tooltipTimers.set(trigger, timer);
      this.timers.add(timer);
    }

    clearTooltipTimer(trigger) {
      const timer = this.tooltipTimers.get(trigger);
      if (timer) {
        window.clearTimeout(timer);
        this.timers.delete(timer);
        this.tooltipTimers.delete(trigger);
      }
    }

    hideTooltip(trigger) {
      if (!trigger) return;
      this.clearTooltipTimer(trigger);
      this.tooltipFor(trigger)?.classList.remove('is-visible');
      trigger.classList.remove('is-tooltip-visible');
      trigger.closest('[data-ld-cert-item]')?.classList.remove('is-tooltip-visible');
    }

    hideAllTooltips() {
      this.root.querySelectorAll('[data-ld-cert-open]').forEach((trigger) => this.hideTooltip(trigger));
    }

    onPointerOver(event) {
      if (!this.hoverQuery.matches) return;
      const trigger = event.target instanceof Element ? event.target.closest('[data-ld-cert-open]') : null;
      if (!trigger || !this.root.contains(trigger) || trigger.contains(event.relatedTarget)) return;
      this.scheduleTooltip(trigger);
    }

    onPointerOut(event) {
      const trigger = event.target instanceof Element ? event.target.closest('[data-ld-cert-open]') : null;
      if (!trigger || !this.root.contains(trigger) || trigger.contains(event.relatedTarget)) return;
      this.hideTooltip(trigger);
    }

    onFocusIn(event) {
      const cert = event.target instanceof Element ? event.target.closest('[data-ld-cert-open]') : null;
      if (cert && this.root.contains(cert)) this.scheduleTooltip(cert);
      const form = event.target instanceof Element
        ? event.target.closest('.ld-home-film__community-form, .ld-community-inline-v3__form')
        : null;
      if (form && this.root.contains(form)) this.emitOnce('community_form_start', {}, 'community:start');
    }

    onFocusOut(event) {
      const cert = event.target instanceof Element ? event.target.closest('[data-ld-cert-open]') : null;
      if (cert && !cert.contains(event.relatedTarget)) this.hideTooltip(cert);
    }

    syncDialogSuspension() {
      const open = (this.knownDialogs || []).some((dialog) => dialog.open || dialog.hasAttribute('open'));
      if (open) this.suspend('dialog');
      else this.resume('dialog');
    }

    onHomeSuspend(event) {
      const source = String(event.detail?.source || 'external').replace(/[^a-z0-9_-]/gi, '').slice(0, 48) || 'external';
      this.suspend(`external:${source}`);
    }

    onHomeResume(event) {
      const source = String(event.detail?.source || 'external').replace(/[^a-z0-9_-]/gi, '').slice(0, 48) || 'external';
      this.resume(`external:${source}`);
    }

    onAssistantState(event) {
      const state = event.detail?.state;
      if (['opening', 'waiting', 'open', 'opened'].includes(state)) this.suspend('assistant');
      else if (['closing', 'closed', 'fallback'].includes(state)) this.resume('assistant');
    }

    onCommunitySubmit(event) {
      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) return;
      if (!navigator.onLine) {
        event.preventDefault();
        this.showOfflineFormError(form);
        this.emit('community_submit', { result: 'error', reason: 'offline' });
        return;
      }
      const button = form.querySelector('[data-ld-community-submit], button[type="submit"]');
      if (button) {
        if (!button.dataset.ldOriginalLabel) button.dataset.ldOriginalLabel = button.textContent.trim();
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Invio…';
      }
    }

    showOfflineFormError(form) {
      let message = form.querySelector('[data-ld-community-offline]');
      if (!message) {
        message = document.createElement('p');
        message.className = 'ld-home-film__form-message is-error';
        message.dataset.ldCommunityOffline = '';
        message.setAttribute('role', 'alert');
        message.setAttribute('tabindex', '-1');
        form.appendChild(message);
      }
      message.textContent = 'Connessione non disponibile. Controlla la rete e riprova.';
      message.focus?.({ preventScroll: true });
    }

    resetCommunitySubmit() {
      this.root.querySelectorAll('[data-ld-community-submit], .ld-community-dialog-v2__form button[type="submit"]').forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (button.dataset.ldOriginalLabel) button.textContent = button.dataset.ldOriginalLabel;
      });
    }

    pauseAutomaticPlaybackForSurface(reason) {
      const playback = this.automaticPlayback;
      if (!playback || playback.surfacePaused) return Boolean(playback);
      if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
      this.automaticFrame = 0;
      playback.surfacePaused = true;
      playback.surfacePauseReason = reason;
      playback.surfacePreviousMediaMode = playback.mediaMode;
      if (playback.kind === 'journey') {
        const currentTime = Number.isFinite(this.video?.currentTime) ? this.video.currentTime : playback.expectedVideoTime;
        playback.expectedVideoTime = clamp(currentTime, playback.mediaStart, playback.mediaEnd);
        this.targetVideoTime = playback.expectedVideoTime;
        this.video?.pause();
        this.stopAutomaticPresentedFrameTracking();
        this.automaticPresentedMediaTime = null;
      } else if (playback.kind === 'return') {
        if (playback.startedAt !== null) {
          playback.elapsed = Math.max(0, Math.min(playback.totalDuration, performance.now() - playback.startedAt));
        }
        playback.startedAt = null;
        this.clearAutomaticSeekQueue();
      }
      playback.mediaMode = 'surface-paused';
      this.root.dataset.automaticMedia = 'surface-paused';
      return true;
    }

    resumeAutomaticPlaybackFromSurface() {
      const playback = this.automaticPlayback;
      if (!playback?.surfacePaused || this.destroyed || this.isSuspended) return false;
      const previousMediaMode = playback.surfacePreviousMediaMode || 'pending';
      delete playback.surfacePaused;
      delete playback.surfacePauseReason;
      delete playback.surfacePreviousMediaMode;

      if (previousMediaMode === 'waiting' || previousMediaMode === 'pending') {
        playback.mediaMode = previousMediaMode;
        this.root.dataset.automaticMedia = previousMediaMode;
        if (this.mediaReady) this.startAutomaticMedia(playback);
        return true;
      }

      if (playback.kind === 'journey') {
        playback.mediaMode = 'native';
        this.root.dataset.automaticMedia = 'native';
        const currentTime = Number.isFinite(this.video?.currentTime) ? this.video.currentTime : playback.expectedVideoTime;
        playback.expectedVideoTime = clamp(currentTime, playback.mediaStart, playback.mediaEnd);
        this.targetVideoTime = playback.expectedVideoTime;
        this.journeyLastMediaTime = playback.expectedVideoTime;
        this.journeyLastProgressAt = performance.now();
        this.setChapterPlaybackRate();
        const startFrames = () => {
          if (this.destroyed || this.isSuspended || this.automaticPlayback !== playback) return;
          if (!this.automaticFrame) this.automaticFrame = requestAnimationFrame((timestamp) => this.runAutomaticPlayback(timestamp));
        };
        const playPromise = this.video?.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(startFrames).catch(() => this.useVideoFallback('chapter_resume_rejected'));
        } else {
          startFrames();
        }
        return true;
      }

      playback.mediaMode = 'reverse-seek';
      this.root.dataset.automaticMedia = 'reverse-seek';
      playback.startedAt = performance.now() - Math.max(0, playback.elapsed || 0);
      if (!this.automaticFrame) this.automaticFrame = requestAnimationFrame((timestamp) => this.runAutomaticPlayback(timestamp));
      return true;
    }

    suspend(reason) {
      if (this.destroyed) return;
      this.suspensions.add(reason);
      const preserveJourney = reason === 'assistant' || reason === 'external:collection-chooser' || reason === 'menu';
      if (preserveJourney) this.pauseAutomaticPlaybackForSurface(reason);
      else this.cancelAutomaticPlayback(reason);
      this.root.classList.add('is-suspended');
      this.hideAllTooltips();
      if (this.introPlaying) this.resumeIntroOnUnsuspend = true;
      this.cancelIntro();
      this.pauseIntroSequence();
      this.video?.pause();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.updateNavigation();
    }

    resume(reason) {
      if (this.destroyed) return;
      this.suspensions.delete(reason);
      this.updateNavigation();
      if (this.suspensions.size) return;
      this.root.classList.remove('is-suspended');
      this.resumeIntroOnUnsuspend = false;
      if (this.automaticPlayback?.surfacePaused) {
        this.resumeAutomaticPlaybackFromSurface();
      } else if (!this.introConsumed && this.isAtIntroStart()) {
        this.prepareStaticHero();
      }
      this.invalidateGeometry();
    }

    get isSuspended() {
      return this.suspensions.size > 0;
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      this.later(() => {
        if (this.liveRegion?.isConnected) this.liveRegion.textContent = message;
      }, 24);
    }

    later(callback, delay) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        callback();
      }, delay);
      this.timers.add(timer);
      return timer;
    }

    emit(name, detail = {}) {
      const payload = eventPayload(name, detail);
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    }

    emitOnce(name, detail = {}, key = name) {
      if (this.eventKeys.has(key)) return;
      this.eventKeys.add(key);
      this.emit(name, detail);
    }

    getState() {
      const worldId = PUBLIC_WORLDS.includes(this.activeWorld) ? this.activeWorld : 'viso';
      const category = safeCategory(worldId);
      const revealed = category && (this.eventKeys.has(`reveal:${category}`) || ['chapter-reveal', 'chapter-stable'].includes(this.activeState));
      const stableProgress = category ? this.config.categories[CATEGORY_IDS.indexOf(category)].stableProgress : 1;
      return Object.freeze({
        worldId,
        phase: this.compatibilityPhase(),
        normalizedProgress: round(revealed ? stableProgress : this.activeLocalProgress),
        scrollY: Math.round(window.scrollY),
      });
    }

    targetForWorld(worldId) {
      if (this.geometryDirty) this.refreshGeometry();
      if (!this.geometry) return null;
      if (worldId === 'epilogue') {
        return this.epilogue ? Math.max(0, this.epilogue.getBoundingClientRect().top + window.scrollY - this.headerHeight()) : this.geometry.scrollEnd;
      }
      const index = CATEGORY_IDS.indexOf(worldId);
      if (index < 0) return null;
      const geometry = this.geometry.categories[index];
      const progress = this.config.categories[index].stableProgress;
      return scrollTargetAtOrAfter(geometry, progress);
    }

    restore(record) {
      if (!record || !PUBLIC_WORLDS.includes(record.worldId)) return false;
      if (this.staticTier) {
        const target = record.worldId === 'epilogue'
          ? this.epilogue
          : this.semanticCategories.find((section) => section.dataset.category === record.worldId);
        if (!target) return false;
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
        const stableProgress = record.worldId === 'epilogue'
          ? 1
          : this.config.categories[CATEGORY_IDS.indexOf(record.worldId)]?.stableProgress || V2_PHASES.categoryStable;
        this.setActive(record.worldId, record.worldId === 'epilogue' ? 'epilogue-stable' : 'chapter-stable', stableProgress, record.worldId === 'epilogue' ? 'epilogue' : 'stable');
        return true;
      }
      const target = this.targetForWorld(record.worldId);
      if (!Number.isFinite(target)) return false;
      this.introConsumed = true;
      this.cancelIntro();
      this.root.classList.add('is-restoring');
      this.previousScrollY = target;
      window.scrollTo({ top: target, behavior: 'instant' });
      if (record.worldId === 'epilogue') {
        this.targetVideoTime = this.config.epilogue.blackTarget;
      } else {
        const index = CATEGORY_IDS.indexOf(record.worldId);
        const category = this.config.categories[index];
        this.lastSegmentIndex = index;
        this.targetVideoTime = this.videoTimeForCategory(category, category.stableProgress);
      }
      this.seekVideo(this.targetVideoTime, { force: true });
      if (record.worldId !== 'epilogue') {
        const stableState = this.stableJourneyStateForWorld(record.worldId);
        if (stableState) this.setJourneyState(stableState);
        this.journeyAnchorY = target;
      }
      this.schedule();
      this.later(() => {
        const correction = this.targetForWorld(record.worldId);
        if (Number.isFinite(correction) && Math.abs(window.scrollY - correction) > 6) {
          this.previousScrollY = correction;
          window.scrollTo({ top: correction, behavior: 'instant' });
        }
        this.root.classList.remove('is-restoring');
        this.schedule();
      }, 320);
      return true;
    }

    refresh() {
      this.invalidateGeometry();
      if (!this.staticTier) this.loadMediaForViewport({ preserveTime: true });
    }

    selectForEditor() {
      this.root.classList.add('is-section-selected', 'is-design-mode', 'is-static');
      this.setSemanticEnhanced(false);
      const category = safeCategory(this.root.dataset.editorPreviewCategory) || 'viso';
      const section = this.semanticCategories.find((element) => element.dataset.category === category) || this.root;
      section.scrollIntoView?.({ block: 'start', behavior: 'instant' });
      const stableProgress = this.config.categories[CATEGORY_IDS.indexOf(category)]?.stableProgress || V2_PHASES.categoryStable;
      this.setActive(category, 'chapter-stable', stableProgress, 'stable');
      this.setSkipVisible(false);
    }

    deselectForEditor() {
      this.root.classList.remove('is-section-selected');
      this.schedule();
    }

    destroy() {
      if (this.destroyed) return;
      this.cancelAutomaticPlayback('destroy', { silent: true });
      this.destroyed = true;
      this.abortController.abort();
      this.unsubscribeCoordinator?.();
      this.unsubscribeCoordinator = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.mediaQueryCleanups.forEach((cleanup) => cleanup());
      this.mediaQueryCleanups = [];
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.introFrame) cancelAnimationFrame(this.introFrame);
      if (this.introSequenceFrame) cancelAnimationFrame(this.introSequenceFrame);
      if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
      this.frame = 0;
      this.introFrame = 0;
      this.introSequenceFrame = 0;
      this.automaticFrame = 0;
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers.clear();
      this.tooltipTimers.clear();
      this.hideAllTooltips();
      this.unloadMedia();
      this.restoreEpilogue();
      this.shellSnapshots.forEach((snapshot, element) => {
        restoreAttribute(element, 'aria-hidden', snapshot.ariaHidden);
        restoreAttribute(element, 'inert', snapshot.inert);
        element.classList.toggle('is-r7-cinematic-hidden', snapshot.hiddenClass);
      });
      this.accessibilitySnapshots.forEach((snapshot, element) => {
        restoreAttribute(element, 'aria-hidden', snapshot.ariaHidden);
        restoreAttribute(element, 'inert', snapshot.inert);
      });
      this.shortcutSnapshots.forEach((snapshot, item) => {
        restoreAttribute(item, 'aria-current', snapshot.ariaCurrent);
        restoreAttribute(item, 'aria-label', snapshot.ariaLabel);
        item.classList.toggle('is-current', snapshot.currentClass);
      });
      this.phraseAttributeSnapshots.forEach((snapshot, phrase) => {
        restoreAttribute(phrase, 'data-position', snapshot.position);
        restoreAttribute(phrase, 'data-motion', snapshot.motion);
        restoreAttribute(phrase, 'aria-hidden', snapshot.ariaHidden);
        phrase.classList.toggle('is-active', snapshot.activeClass);
      });
      this.navigationSnapshots.forEach((snapshot, button) => {
        restoreAttribute(button, 'disabled', snapshot.disabled);
        restoreAttribute(button, 'aria-label', snapshot.ariaLabel);
        restoreAttribute(button, 'aria-busy', snapshot.ariaBusy);
      });
      if (this.navigationCurrent) this.navigationCurrent.textContent = this.navigationCurrentBefore;
      this.skipLabelSnapshots.forEach((text, label) => { label.textContent = text; });
      restoreAttribute(this.skip, 'aria-label', this.skipAriaLabelBefore);
      if (this.skip) {
        this.skip.hidden = this.skipHiddenBefore;
        this.skip.classList.toggle('is-visible', this.skipVisibleClassBefore);
      }
      this.introActions?.classList.toggle('is-visible', this.introActionsVisibleClassBefore);
      this.scrollCue?.classList.toggle('is-visible', this.scrollCueVisibleClassBefore);
      this.visualCategories.forEach((element) => element.classList.remove('is-active'));
      this.identity?.classList.remove('is-active');
      this.curtain?.classList.remove('is-visible');
      this.title?.classList.remove('is-visible');
      this.signature?.classList.remove('is-visible');
      this.epilogue?.classList.remove('is-active');
      if (this.title) this.title.textContent = this.titleBefore;
      STYLE_PROPERTIES.forEach((property) => this.root.style.removeProperty(property));
      restoreAttribute(this.root, 'data-active-world', this.rootSnapshot.activeWorld);
      restoreAttribute(this.root, 'data-active-phase', this.rootSnapshot.activePhase);
      restoreAttribute(this.root, 'data-state', this.rootSnapshot.state);
      restoreAttribute(this.root, 'data-visual-state', this.rootSnapshot.visualState);
      restoreAttribute(this.root, 'data-tier', this.rootSnapshot.tier);
      restoreAttribute(this.root, 'data-direction', this.rootSnapshot.direction);
      [
        'is-enhanced', 'is-static', 'is-reduced-motion', 'is-low-data', 'is-low-capability', 'is-short-viewport', 'is-restrained-effects',
        'is-design-mode', 'is-duplicate-home', 'is-suspended',
        'is-video-fallback', 'is-video-ready', 'is-video-seeking', 'has-video-frame',
        'has-intro-controls', 'has-intro-title-complete', 'is-restoring', 'is-section-selected',
      ].forEach((className) => this.root.classList.remove(className));
      delete this.root.dataset.ldHomeFilmReady;
      delete this.root.dataset.ldHomeStateOwner;
      delete this.root.dataset.ldHomeMediaOwner;
      delete this.root.dataset.mediaViewport;
      delete this.root.dataset.journeyState;
      delete this.root.dataset.transitionInProgress;
      delete this.root.ldHomeFilmController;
      if (document.body) {
        if (this.bodyToneBefore === null) document.body.removeAttribute('data-dv2-header-tone');
        else document.body.setAttribute('data-dv2-header-tone', this.bodyToneBefore);
      }
      CONNECTED.delete(this);
    }
  }


  /* ========================================================================
     DIVETTA MILANO — HOME EXECUTION CONTRACT v1.2 runtime
     Owner-scoped repair layer. Installed before the first controller instance.
     ======================================================================== */
  const LD_V11 = Object.freeze({
    wheelIdleMs: 900,
    wheelDecisive: 180,
    wheelDecisiveWindow: 350,
    wheelGestureMin: 48,
    wheelDoubleWindow: 850,
    wheelMicro: 8,
    touchMin: 48,
    touchDecisive: 96,
    touchIdleMs: 1000,
    categoryTitleMs: 1100,
    phraseHolds: Object.freeze([1800, 1900, 1800, 1800]),
    phraseTransitionMs: 320,
    phraseOutMs: 280,
    portalCrossfadeMs: 450,
    revealMs: 620,
  });

  const v11Legacy = Object.freeze({
    connect: LDHomeFilmController.prototype.connect,
    destroy: LDHomeFilmController.prototype.destroy,
    refreshGeometry: LDHomeFilmController.prototype.refreshGeometry,
    revealIntroControls: LDHomeFilmController.prototype.revealIntroControls,
    onJourneyWheel: LDHomeFilmController.prototype.onJourneyWheel,
    onJourneyTouchStart: LDHomeFilmController.prototype.onJourneyTouchStart,
    onJourneyTouchMove: LDHomeFilmController.prototype.onJourneyTouchMove,
    onJourneyTouchEnd: LDHomeFilmController.prototype.onJourneyTouchEnd,
    onScroll: LDHomeFilmController.prototype.onScroll,
    startJourneyTransition: LDHomeFilmController.prototype.startJourneyTransition,
    runAutomaticPlayback: LDHomeFilmController.prototype.runAutomaticPlayback,
    monitorJourneyMarker: LDHomeFilmController.prototype.monitorJourneyMarker,
    renderPhrase: LDHomeFilmController.prototype.renderPhrase,
    finalizeStableWorld: LDHomeFilmController.prototype.finalizeStableWorld,
    clearAutomaticPlaybackState: LDHomeFilmController.prototype.clearAutomaticPlaybackState,
    renderVisualCategory: LDHomeFilmController.prototype.renderVisualCategory,
    setAllVisualCategoriesInactive: LDHomeFilmController.prototype.setAllVisualCategoriesInactive,
    suspend: LDHomeFilmController.prototype.suspend,
    resume: LDHomeFilmController.prototype.resume,
    onRootClick: LDHomeFilmController.prototype.onRootClick,
    setActive: LDHomeFilmController.prototype.setActive,
    jumpToStableWorld: LDHomeFilmController.prototype.jumpToStableWorld,
    pauseAutomaticPlaybackForSurface: LDHomeFilmController.prototype.pauseAutomaticPlaybackForSurface,
    resumeAutomaticPlaybackFromSurface: LDHomeFilmController.prototype.resumeAutomaticPlaybackFromSurface,
  });

  LDHomeFilmController.prototype.v11PlaybackRate = function () {
    return 1;
  };

  LDHomeFilmController.prototype.setChapterPlaybackRate = function () {
    if (!this.video) return;
    try {
      this.video.defaultPlaybackRate = 1;
      this.video.playbackRate = 1;
      this.root.dataset.requiredPlaybackRate = '1';
    } catch (_error) {}
  };

  LDHomeFilmController.prototype.syncHomeStageGeometryV11 = function () {
    if (!this.stage?.isConnected) return;
    const viewportWidth = Math.max(window.innerWidth || document.documentElement.clientWidth || 1, 1);
    const viewportHeight = Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);
    const stageRect = this.stage.getBoundingClientRect();
    const stageTop = clamp(stageRect.top, 0, viewportHeight);
    const stageBottom = clamp(stageRect.bottom, stageTop, viewportHeight);
    const stageHeight = Math.max(1, stageBottom - stageTop);
    const stageMidpoint = stageTop + stageHeight / 2;
    const visibleRect = (selector) => {
      const node = document.querySelector(selector);
      if (!(node instanceof HTMLElement)) return null;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return null;
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 ? rect : null;
    };
    const leftRail = visibleRect('.ld-floating-shortcuts--dv2-home .ld-floating-shortcuts__rail--home-left');
    const rightRail = visibleRect('.ld-floating-shortcuts--dv2-home .ld-floating-shortcuts__rail--home-right');
    const mobileRail = visibleRect('.ld-floating-shortcuts--dv2-home .ld-floating-shortcuts__rail--home-mobile-left');
    const quickIndex = visibleRect('.ld-home-index__trigger:not([hidden])');
    const contentSafeLeft = leftRail ? Math.min(viewportWidth * 0.34, leftRail.right + 16) : 12;
    const contentSafeRight = rightRail ? Math.min(viewportWidth * 0.34, viewportWidth - rightRail.left + 16) : 12;
    const contentSafeTop = quickIndex
      ? Math.max(22, Math.min(stageHeight * 0.28, quickIndex.bottom - stageTop + 12))
      : 22;
    const bottomSafeZone = mobileRail ? Math.max(72, viewportHeight - mobileRail.top + 10) : 24;
    const owner = this.root.dataset.sectionId || this.root.id || 'home';
    const style = document.documentElement.style;
    document.documentElement.dataset.ldHomeGeometryOwner = owner;
    style.setProperty('--ld-home-stage-top', `${Math.round(stageTop * 100) / 100}px`);
    style.setProperty('--ld-home-stage-bottom', `${Math.round(stageBottom * 100) / 100}px`);
    style.setProperty('--ld-home-stage-height', `${Math.round(stageHeight * 100) / 100}px`);
    style.setProperty('--ld-home-stage-midpoint-y', `${Math.round(stageMidpoint * 100) / 100}px`);
    style.setProperty('--ld-home-content-safe-left', `${Math.round(contentSafeLeft * 100) / 100}px`);
    style.setProperty('--ld-home-content-safe-right', `${Math.round(contentSafeRight * 100) / 100}px`);
    style.setProperty('--ld-home-content-safe-top', `${Math.round(contentSafeTop * 100) / 100}px`);
    style.setProperty('--ld-home-bottom-safe-zone', `${Math.round(bottomSafeZone * 100) / 100}px`);
  };

  LDHomeFilmController.prototype.clearHomeStageGeometryV11 = function () {
    const owner = this.root.dataset.sectionId || this.root.id || 'home';
    if (document.documentElement.dataset.ldHomeGeometryOwner !== owner) return;
    delete document.documentElement.dataset.ldHomeGeometryOwner;
    const style = document.documentElement.style;
    ['--ld-home-stage-top','--ld-home-stage-bottom','--ld-home-stage-height','--ld-home-stage-midpoint-y','--ld-home-content-safe-left','--ld-home-content-safe-right','--ld-home-content-safe-top','--ld-home-bottom-safe-zone'].forEach((property) => style.removeProperty(property));
  };

  LDHomeFilmController.prototype.refreshGeometry = function () {
    const result = v11Legacy.refreshGeometry.call(this);
    this.syncHomeStageGeometryV11();
    if (CATEGORY_IDS.includes(this.activeWorld)) this.setupQualityMarqueeV11(this.visualFor(this.activeWorld));
    return result;
  };

  LDHomeFilmController.prototype.connect = function () {
    if (!this.headerThemeV11Captured) {
      this.headerThemeBefore = document.documentElement.getAttribute('data-r7-header-theme');
      this.headerThemeV11Captured = true;
      document.documentElement.dataset.r7HeaderTheme = 'dark';
    }
    const result = v11Legacy.connect.call(this);
    this.initHomeIndexV11();
    this.syncPortalLifecycleV11('', 'viso');
    return result;
  };

  LDHomeFilmController.prototype.destroy = function () {
    this.destroyHomeIndexV11?.();
    this.clearHomeStageGeometryV11?.();
    CATEGORY_IDS.forEach((world) => this.releasePortalV11?.(world, { unload: true }));
    const result = v11Legacy.destroy.call(this);
    if (this.headerThemeV11Captured) {
      if (this.headerThemeBefore === null) document.documentElement.removeAttribute('data-r7-header-theme');
      else document.documentElement.dataset.r7HeaderTheme = this.headerThemeBefore;
      this.headerThemeV11Captured = false;
    }
    return result;
  };


  LDHomeFilmController.prototype.setActive = function (world, state, localProgress, visualState) {
    const result = v11Legacy.setActive.call(this, world, state, localProgress, visualState);
    if (document.documentElement.dataset.r7HeaderTheme !== 'dark') document.documentElement.dataset.r7HeaderTheme = 'dark';
    if (document.body) {
      if (document.body.dataset.dv2HeaderTone !== 'dark') document.body.dataset.dv2HeaderTone = 'dark';
      document.body.classList.toggle('ld-home-past-hero', world !== 'handoff');
    }
    if (CATEGORY_IDS.includes(world) && state === 'chapter-stable' && !this.automaticPlayback) {
      this.setHomeIndexTriggerVisibleV11(true);
    }
    return result;
  };

  LDHomeFilmController.prototype.buildContractSequenceV11 = function (_world, category) {
    const stages = [];
    let at = 0;

    const add = (id, duration, detail = {}) => {
      const stage = Object.freeze({
        id,
        startAt: at,
        endAt: at + duration,
        duration,
        ...detail,
      });
      stages.push(stage);
      at += duration;
      return stage;
    };

    add('category-title', LD_V11.categoryTitleMs);

    LD_V11.phraseHolds.forEach((holdMs, index) => {
      const phraseIndex = index + 1;
      add(`phrase-${phraseIndex}`, holdMs, {
        phraseIndex,
        holdMs,
      });

      if (phraseIndex < 4) {
        add(`morph-${phraseIndex}-${phraseIndex + 1}`, LD_V11.phraseTransitionMs, {
          fromPhrase: phraseIndex,
          toPhrase: phraseIndex + 1,
        });
      }
    });

    add('phrase-out', LD_V11.phraseOutMs, { fromPhrase: 4 });
    add('reveal', LD_V11.revealMs);

    return Object.freeze({
      stages: Object.freeze(stages),
      totalDuration: at,
      mediaDuration: Math.max(
        0,
        category.videoEnd - category.videoStart - FRAME_DURATION
      ) * 1000,
      holds: LD_V11.phraseHolds,
    });
  };

  LDHomeFilmController.prototype.phraseTransitionMsV11 = function () {
    return LD_V11.phraseTransitionMs;
  };

  LDHomeFilmController.prototype.progressForContractStageV11 = function (stage, local) {
    const t = clamp(local);

    if (stage.id === 'phrase-1') {
      return lerp(V2_PHASES.phrase1Start, V2_PHASES.phrase1End, t);
    }
    if (stage.id === 'morph-1-2') {
      return lerp(V2_PHASES.phrase1End, V2_PHASES.phrase2Start, t);
    }
    if (stage.id === 'phrase-2') {
      return lerp(V2_PHASES.phrase2Start, V2_PHASES.phrase2End, t);
    }
    if (stage.id === 'morph-2-3') {
      return lerp(V2_PHASES.phrase2End, V2_PHASES.phrase3Start, t);
    }
    if (stage.id === 'phrase-3') {
      return lerp(V2_PHASES.phrase3Start, V2_PHASES.phrase3End, t);
    }
    if (stage.id === 'morph-3-4') {
      return lerp(V2_PHASES.phrase3End, V2_PHASES.phrase4Start, t);
    }
    if (stage.id === 'phrase-4') {
      return lerp(V2_PHASES.phrase4Start, V2_PHASES.phrase4End, t);
    }
    if (stage.id === 'phrase-out') {
      return lerp(V2_PHASES.phrase4End, V2_PHASES.breathingEnd, t);
    }
    if (stage.id === 'reveal') {
      return lerp(V2_PHASES.portalOpenStart, V2_PHASES.categoryStable, t);
    }

    return V2_PHASES.phrase1Start;
  };

  LDHomeFilmController.prototype.timingLogV11 = function (entry) {
    const record = Object.freeze({ timestamp: performance.now(), ...entry });
    this.contractTimingLogV11 = this.contractTimingLogV11 || [];
    this.contractTimingLogV11.push(record);
    window.__LDHomeTimingLog = window.__LDHomeTimingLog || [];
    window.__LDHomeTimingLog.push(record);
    this.root.dataset.lastTimingDelay = Number.isFinite(record.delayMs) ? String(Math.round(record.delayMs * 100) / 100) : '';
  };

  LDHomeFilmController.prototype.clearPhraseClassesV11 = function (except = null) {
    this.phrases.forEach((phrase) => {
      if (phrase === except) return;
      phrase.classList.remove('is-active','is-past','is-entering','is-exiting');
      phrase.setAttribute('aria-hidden','true');
    });
    if (!except) this.phraseLayer?.classList.remove('is-reading');
  };

  LDHomeFilmController.prototype.renderContractTitleV12 = function (world, local) {
    const t = clamp(local);
    const title = this.titleFor(world);

    if (this.title && this.title.textContent !== title) {
      this.title.textContent = title;
    }

    this.clearPhraseClassesV11();
    this.setAllVisualCategoriesInactive();

    this.curtain?.classList.add('is-visible');
    this.title?.classList.add('is-visible');
    this.signature?.classList.add('is-visible');

    const enter = clamp(t / 0.38);
    const hold = t >= 0.38 && t < 0.66;
    const exit = clamp((t - 0.66) / 0.34);

    let opacity;
    let scale;
    let blur;
    let curtainOpacity;

    if (t < 0.38) {
      opacity = easeInOutCubic(enter);
      scale = lerp(0.72, 1, easeInOutCubic(enter));
      blur = lerp(12, 0, easeInOutCubic(enter));
      curtainOpacity = 1;
    } else if (hold) {
      opacity = 1;
      scale = 1;
      blur = 0;
      curtainOpacity = 1;
    } else {
      opacity = 1 - easeInOutCubic(exit);
      scale = lerp(1, 2.8, easeInOutCubic(exit));
      blur = lerp(0, 8, easeInOutCubic(exit));
      curtainOpacity = 1 - easeInOutCubic(exit);
    }

    this.writeStyle('--ld-film-title-opacity', round(opacity));
    this.writeStyle('--ld-v2-title-depth-scale', round(scale));
    this.writeStyle('--ld-v2-title-blur', `${round(blur)}px`);
    this.writeStyle('--ld-film-curtain-opacity', round(curtainOpacity));
  };

  LDHomeFilmController.prototype.clearContractTitleV12 = function () {
    this.curtain?.classList.remove('is-visible');
    this.title?.classList.remove('is-visible');
    this.signature?.classList.remove('is-visible');
    this.writeStyle('--ld-film-title-opacity', 0);
    this.writeStyle('--ld-film-curtain-opacity', 0);
    this.writeStyle('--ld-v2-title-blur', '0px');
    this.writeStyle('--ld-v2-title-depth-scale', 1);
  };

  LDHomeFilmController.prototype.activatePhraseV11 = function (world, index, mode = 'hold') {
    const incoming = this.phrases.find((phrase) => phrase.dataset.category === world && finite(phrase.dataset.index,0) === index);
    if (!incoming) return;
    if (this.contractPhraseActiveV11 === incoming && mode === 'hold') return;
    this.clearPhraseClassesV11(incoming);
    incoming.classList.add('is-active');
    incoming.classList.toggle('is-entering', mode === 'enter');
    incoming.setAttribute('aria-hidden','true');
    this.contractPhraseActiveV11 = incoming;
    this.phraseLayer?.classList.add('is-reading');
  };

  LDHomeFilmController.prototype.morphPhraseV11 = function (world, fromIndex, toIndex, playback, stage) {
    if (playback.v11StageStarted === stage.id) return;
    playback.v11StageStarted = stage.id;
    const outgoing = this.phrases.find((phrase) => phrase.dataset.category === world && finite(phrase.dataset.index,0) === fromIndex);
    const incoming = this.phrases.find((phrase) => phrase.dataset.category === world && finite(phrase.dataset.index,0) === toIndex);
    const now = performance.now();
    const expected = (playback.startedAt || now) + stage.startAt;
    this.timingLogV11({ world, boundaryFrom: fromIndex, boundaryTo: toIndex, expectedAt: expected, triggeredAt: now, delayMs: Math.max(0, now - expected) });
    if (outgoing) {
      outgoing.classList.remove('is-entering');
      outgoing.classList.add('is-active','is-exiting');
    }
    if (incoming) {
      incoming.classList.remove('is-past','is-exiting');
      incoming.classList.add('is-active','is-entering');
      incoming.setAttribute('aria-hidden','true');
    }
    this.contractPhraseActiveV11 = incoming || outgoing || null;
    this.phraseLayer?.classList.add('is-reading');
    this.later(() => {
      outgoing?.classList.remove('is-active','is-exiting');
      incoming?.classList.remove('is-entering');
      if (incoming) incoming.classList.add('is-active');
    }, this.phraseTransitionMsV11());
  };

  LDHomeFilmController.prototype.morphPhraseOutV11 = function (world, playback, stage) {
    if (playback.v11StageStarted === stage.id) return;
    playback.v11StageStarted = stage.id;

    const outgoing = this.phrases.find(
      (phrase) =>
        phrase.dataset.category === world &&
        finite(phrase.dataset.index, 0) === 4
    );

    const now = performance.now();
    const expected = (playback.startedAt || now) + stage.startAt;

    this.timingLogV11({
      world,
      boundaryFrom: 4,
      boundaryTo: 0,
      expectedAt: expected,
      triggeredAt: now,
      delayMs: Math.max(0, now - expected),
    });

    outgoing?.classList.add('is-active','is-exiting');

    this.later(() => {
      outgoing?.classList.remove('is-active','is-exiting');
      this.phraseLayer?.classList.remove('is-reading');
    }, LD_V11.phraseOutMs);

    this.contractPhraseActiveV11 = null;
  };

  LDHomeFilmController.prototype.renderPhrase = function (category, progress) {
    if (this.contractPhraseOverrideV11 && this.contractPhraseOverrideV11 === category) return;
    return v11Legacy.renderPhrase.call(this, category, progress);
  };

  LDHomeFilmController.prototype.startJourneyTransition = function ({ direction, sourceWorld, targetWorld, method }) {
    if (direction !== 'next' || !CATEGORY_IDS.includes(targetWorld)) {
      return v11Legacy.startJourneyTransition.call(this, { direction, sourceWorld, targetWorld, method });
    }
    const targetIndex = CATEGORY_IDS.indexOf(targetWorld);
    const targetCategory = this.config.categories[targetIndex];
    if (!targetCategory || this.transitionInProgress || this.automaticPlayback) return false;
    const sequence = this.buildContractSequenceV11(targetWorld, targetCategory);
    const playback = {
      kind: 'journey', direction, sourceWorld, targetWorld, renderWorld: targetWorld,
      mediaStart: targetCategory.videoStart, mediaEnd: targetCategory.videoEnd,
      totalDuration: sequence.totalDuration, targetY: this.targetForWorld(targetWorld),
      sourceY: this.journeyAnchorY, method, mediaMode:'pending', startedAt:null,
      expectedVideoTime:targetCategory.videoStart, contractV11:true, sequence,
      portalHandoff:false, v11StageStarted:'',
    };
    this.emit('chapter_navigation_next', { category: targetWorld, from_state: this.journeyState, method, world_id: targetWorld });
    this.contractPhraseOverrideV11 = targetWorld;
    const started = this.beginAutomaticPlayback(playback);
    if (started) this.installVideoFrameMonitorV12(playback);
    return started;
  };

  LDHomeFilmController.prototype.stageForElapsedV11 = function (playback, elapsed) {
    return playback.sequence.stages.find((stage) => elapsed < stage.endAt) || playback.sequence.stages[playback.sequence.stages.length - 1];
  };

  LDHomeFilmController.prototype.installVideoFrameMonitorV12 = function (playback) {
    const video = this.video;
    if (!video || typeof video.requestVideoFrameCallback !== 'function') return;

    const tick = (_now, metadata) => {
      if (this.automaticPlayback !== playback || playback.portalHandoff) return;

      const mediaTime = Number(metadata?.mediaTime ?? video.currentTime);

      if (mediaTime >= playback.mediaEnd - FRAME_DURATION * 0.75) {
        this.activatePortalHandoffV11(playback.targetWorld, playback);
        return;
      }

      playback.videoFrameRequest = video.requestVideoFrameCallback(tick);
    };

    playback.videoFrameRequest = video.requestVideoFrameCallback(tick);
  };

  LDHomeFilmController.prototype.holdNarrativeForBufferV12 = function (playback, timestamp) {
    if (playback.portalHandoff || !this.video || this.video.paused) return false;

    const mediaTime = Number.isFinite(this.automaticPresentedMediaTime)
      ? this.automaticPresentedMediaTime
      : this.video.currentTime;

    if (!Number.isFinite(playback.lastNarrativeMediaTime) || mediaTime > playback.lastNarrativeMediaTime + 0.004) {
      playback.lastNarrativeMediaTime = mediaTime;
      playback.lastNarrativeMediaProgressAt = timestamp;

      if (playback.narrativeBufferStartedAt) {
        playback.startedAt += timestamp - playback.narrativeBufferStartedAt;
        playback.narrativeBufferStartedAt = 0;
      }

      return false;
    }

    const stalledFor = timestamp - (playback.lastNarrativeMediaProgressAt || timestamp);
    const waitingForData = this.video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;

    if (waitingForData && stalledFor >= 180) {
      if (!playback.narrativeBufferStartedAt) playback.narrativeBufferStartedAt = timestamp;
      return true;
    }

    return false;
  };

  LDHomeFilmController.prototype.portalVideoForV11 = function (world) {
    return this.visualFor(world)?.querySelector('[data-ld-portal-video]') || null;
  };

  LDHomeFilmController.prototype.prepareNearPortalV11 = function (world) {
    const video = this.portalVideoForV11(world);
    if (!video || video.getAttribute('src') || !video.dataset.src) return;
    video.src = video.dataset.src;
    video.muted = true; video.defaultMuted = true; video.loop = true; video.playsInline = true; video.preload = 'metadata';
    video.defaultPlaybackRate = 1; video.playbackRate = 1;
    try { video.load(); } catch (_error) {}
  };

  LDHomeFilmController.prototype.releasePortalV11 = function (world, { unload = true } = {}) {
    const visual = this.visualFor(world);
    const video = this.portalVideoForV11(world);
    if (!video) return;
    video.pause();
    visual?.classList.remove('is-portal-playing','is-portal-under-phrases','is-portal-crossfading');
    if (unload && video.getAttribute('src')) {
      video.removeAttribute('src');
      try { video.load(); } catch (_error) {}
    }
  };

  LDHomeFilmController.prototype.syncPortalLifecycleV11 = function (activeWorld = '', nearWorld = '') {
    CATEGORY_IDS.forEach((world) => {
      const video = this.portalVideoForV11(world);
      if (!video) return;
      if (world === activeWorld) {
        this.prepareNearPortalV11(world);
        return;
      }
      if (world === nearWorld) {
        this.prepareNearPortalV11(world);
        video.pause();
        this.visualFor(world)?.classList.remove('is-portal-playing','is-portal-under-phrases','is-portal-crossfading');
        return;
      }
      this.releasePortalV11(world, { unload: true });
    });
  };

  LDHomeFilmController.prototype.pauseInactivePortalVideosV11 = function (keepWorld = '') {
    CATEGORY_IDS.forEach((world) => {
      const visual = this.visualFor(world);
      const video = this.portalVideoForV11(world);
      if (!video || world === keepWorld) return;
      video.pause();
      visual?.classList.remove('is-portal-playing','is-portal-under-phrases','is-portal-crossfading');
    });
  };

  LDHomeFilmController.prototype.activatePortalHandoffV11 = function (world, playback = this.automaticPlayback) {
    if (!CATEGORY_IDS.includes(world) || playback?.portalHandoff) return;
    if (playback) {
      playback.portalHandoff = true;
      if (
        this.video &&
        playback.videoFrameRequest &&
        typeof this.video.cancelVideoFrameCallback === 'function'
      ) {
        this.video.cancelVideoFrameCallback(playback.videoFrameRequest);
        playback.videoFrameRequest = 0;
      }
    }
    const visual = this.visualFor(world);
    const portal = this.portalVideoForV11(world);
    this.video?.pause();
    this.pauseInactivePortalVideosV11(world);
    if (!portal?.dataset.src) {
      this.root.dataset.mediaWarning = 'WARNING_MEDIA_FALLBACK';
      visual?.classList.add('is-portal-fallback');
      return;
    }
    this.prepareNearPortalV11(world);
    portal.defaultPlaybackRate = 1;
    portal.playbackRate = 1;
    visual?.classList.toggle('is-portal-under-phrases', Boolean(playback?.contractV11));
    visual?.classList.add('is-portal-crossfading');
    this.later(() => visual?.classList.remove('is-portal-crossfading'), LD_V11.portalCrossfadeMs);
    const promise = portal.play();
    if (promise && typeof promise.then === 'function') {
      promise.then(() => visual?.classList.add('is-portal-playing')).catch(() => {
        this.root.dataset.mediaWarning = 'WARNING_MEDIA_FALLBACK';
        visual?.classList.add('is-portal-fallback');
      });
    } else visual?.classList.add('is-portal-playing');
  };

  LDHomeFilmController.prototype.monitorJourneyMarker = function () {
    const playback = this.automaticPlayback;
    if (playback?.contractV11 && playback.kind === 'journey' && this.video) {
      if (this.video.currentTime >= playback.mediaEnd - FRAME_DURATION * .75) {
        this.activatePortalHandoffV11(playback.targetWorld, playback);
      }
      return;
    }
    return v11Legacy.monitorJourneyMarker.call(this);
  };

  LDHomeFilmController.prototype.runAutomaticPlayback = function (timestamp) {
    const playback = this.automaticPlayback;
    if (!playback?.contractV11) return v11Legacy.runAutomaticPlayback.call(this, timestamp);

    this.automaticFrame = 0;
    if (this.destroyed || this.isSuspended) return;

    if (playback.startedAt === null) {
      playback.startedAt = timestamp;
      playback.v11StageStarted = '';
      playback.lastNarrativeMediaProgressAt = timestamp;
      this.timingLogV11({ world: playback.targetWorld, event:'sequence-start', startedAt:timestamp, playbackRate:this.video?.playbackRate || this.v11PlaybackRate() });
    }

    if (this.holdNarrativeForBufferV12(playback, timestamp)) {
      this.automaticFrame = requestAnimationFrame(
        (nextTimestamp) => this.runAutomaticPlayback(nextTimestamp)
      );
      return;
    }

    const elapsed = Math.max(0, timestamp - playback.startedAt);
    const stage = this.stageForElapsedV11(playback, elapsed);
    const local = clamp((elapsed - stage.startAt) / Math.max(1, stage.duration));
    const world = playback.targetWorld;
    const index = CATEGORY_IDS.indexOf(world);
    const progress = this.progressForContractStageV11(stage, local);

    if (stage.id === 'category-title') {
      this.renderContractTitleV12(world, local);

      const atSegmentEnd =
        this.video &&
        this.video.currentTime >= playback.mediaEnd - FRAME_DURATION * 0.75;

      if (!playback.portalHandoff && atSegmentEnd) {
        this.activatePortalHandoffV11(world, playback);
      }

      if (elapsed >= playback.totalDuration - 1) {
        this.finishAutomaticPlayback();
        return;
      }

      this.automaticFrame = requestAnimationFrame(
        (nextTimestamp) => this.runAutomaticPlayback(nextTimestamp)
      );
      return;
    }

    this.clearContractTitleV12();

    if (stage.id.startsWith('phrase-')) {
      if (stage.phraseIndex) {
        if (playback.v11StageStarted !== stage.id) {
          playback.v11StageStarted = stage.id;
          this.activatePhraseV11(world, stage.phraseIndex, stage.phraseIndex === 1 ? 'enter' : 'hold');
          const holdStart = timestamp;
          this.timingLogV11({ world, phraseIndex:stage.phraseIndex, event:'hold-start', holdMs:stage.holdMs, holdStartedAt:holdStart, holdExpectedEnd:holdStart + stage.holdMs });
        }
      } else if (stage.id === 'phrase-out') this.morphPhraseOutV11(world, playback, stage);
    } else if (stage.id.startsWith('morph-')) {
      this.morphPhraseV11(world, stage.fromPhrase, stage.toPhrase, playback, stage);
    }

    this.renderForward(index, progress);
    this.clearContractTitleV12();

    if (playback.portalHandoff && stage.id !== 'reveal') {
      this.renderVisualCategory(world, true, false);
      this.visualFor(world)?.classList.add('is-portal-under-phrases');
    }
    if (stage.id === 'reveal') {
      this.renderVisualCategory(world, true, local > .78);
      this.visualFor(world)?.classList.remove('is-portal-under-phrases');
    }

    const atSegmentEnd =
      this.video &&
      this.video.currentTime >= playback.mediaEnd - FRAME_DURATION * 0.75;

    if (!playback.portalHandoff && atSegmentEnd) {
      this.activatePortalHandoffV11(world, playback);
    }

    if (elapsed >= playback.totalDuration - 1) {
      this.finishAutomaticPlayback();
      return;
    }

    this.automaticFrame = requestAnimationFrame(
      (nextTimestamp) => this.runAutomaticPlayback(nextTimestamp)
    );
  };

  LDHomeFilmController.prototype.clearAutomaticPlaybackState = function () {
    const playback = this.automaticPlayback;

    if (
      this.video &&
      playback?.videoFrameRequest &&
      typeof this.video.cancelVideoFrameCallback === 'function'
    ) {
      this.video.cancelVideoFrameCallback(playback.videoFrameRequest);
      playback.videoFrameRequest = 0;
    }

    this.clearContractTitleV12();
    const result = v11Legacy.clearAutomaticPlaybackState.call(this);
    this.contractPhraseOverrideV11 = '';
    this.contractPhraseActiveV11 = null;
    this.phraseLayer?.classList.remove('is-reading');
    return result;
  };

  LDHomeFilmController.prototype.setupQualityMarqueeV11 = function (visual) {
    const quality = visual?.querySelector('[data-ld-quality-rail]');
    const list = quality?.querySelector('.ld-home-film__quality-list');
    if (!quality || !list) return;
    const width = quality.clientWidth;
    const originals = Array.from(list.children).filter((node) => !node.hasAttribute('data-ld-marquee-clone'));
    const signature = `${width}:${originals.length}`;
    const removeClones = () => list.querySelectorAll('[data-ld-marquee-clone]').forEach((node) => node.remove());
    const disable = () => {
      quality.classList.remove('is-marquee-running');
      quality.style.removeProperty('--ld-quality-marquee-shift');
      quality.style.removeProperty('--ld-quality-marquee-duration');
      removeClones();
      delete list.dataset.ldMarqueeReady;
      delete list.dataset.ldMarqueeSize;
    };
    if (this.reducedMotionQuery.matches || this.mobileQuery.matches) { disable(); return; }
    if (list.dataset.ldMarqueeReady === 'true' && list.dataset.ldMarqueeSize === signature) return;
    removeClones();
    const needsMarquee = originals.length > 1 && list.scrollWidth > width + 8;
    if (!needsMarquee) { disable(); return; }
    const fragment = document.createDocumentFragment();
    originals.forEach((node) => {
      const clone = node.cloneNode(true);
      clone.dataset.ldMarqueeClone = 'true';
      clone.setAttribute('aria-hidden','true');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      fragment.appendChild(clone);
    });
    list.appendChild(fragment);
    list.dataset.ldMarqueeReady = 'true';
    list.dataset.ldMarqueeSize = signature;
    requestAnimationFrame(() => {
      const distance = Math.max(1, list.scrollWidth / 2);
      const speed = 34;
      quality.style.setProperty('--ld-quality-marquee-shift', `${-distance}px`);
      quality.style.setProperty('--ld-quality-marquee-duration', `${distance / speed}s`);
      quality.classList.add('is-marquee-running');
    });
  };

  LDHomeFilmController.prototype.finalizeStableWorld = function (world, options = {}) {
    const result = v11Legacy.finalizeStableWorld.call(this, world, options);
    if (CATEGORY_IDS.includes(world)) {
      const category = this.config.categories[CATEGORY_IDS.indexOf(world)];
      if (this.video && category) {
        this.video.pause();
        this.targetVideoTime = Math.max(category.videoStart, category.videoEnd - FRAME_DURATION);
        this.seekVideo(this.targetVideoTime, { force: true });
      }
      this.pauseInactivePortalVideosV11(world);
      this.activatePortalHandoffV11(world, null);
      const visual = this.visualFor(world);
      visual?.classList.remove('is-portal-under-phrases');
      visual?.classList.add('is-editorial-entering');
      const continueCue = visual?.querySelector('[data-ld-continue-cue]');
      const nextIndex = CATEGORY_IDS.indexOf(world) + 1;
      const nearWorld = nextIndex < CATEGORY_IDS.length ? CATEGORY_IDS[nextIndex] : '';
      if (continueCue) {
        continueCue.classList.add('is-entering');
        const label = continueCue.querySelector('[data-ld-continue-label]');
        if (label) label.textContent = nearWorld ? `SCOPRI ${this.titleFor(nearWorld)}` : 'VAI AL FINALE';
        continueCue.setAttribute('aria-label', nearWorld ? `Scopri il capitolo ${this.titleFor(nearWorld)}` : 'Vai al finale');
      }
      this.later(() => this.setupQualityMarqueeV11(visual), 2000);
      this.later(() => visual?.classList.remove('is-editorial-entering'), 2200);
      this.later(() => this.setHomeIndexTriggerVisibleV11(true), 1750);
      this.syncPortalLifecycleV11(world, nearWorld);
    } else if (world === 'epilogue') this.syncPortalLifecycleV11('', '');
    return result;
  };

  LDHomeFilmController.prototype.renderVisualCategory = function (category, visible, interactive = false) {
    const result = v11Legacy.renderVisualCategory.call(this, category, visible, interactive);
    if (!visible) this.visualFor(category)?.classList.remove('is-portal-under-phrases');
    return result;
  };

  LDHomeFilmController.prototype.setAllVisualCategoriesInactive = function () {
    const result = v11Legacy.setAllVisualCategoriesInactive.call(this);
    if (!this.automaticPlayback?.portalHandoff) this.syncPortalLifecycleV11('', '');
    return result;
  };

  LDHomeFilmController.prototype.armContinueCueV11 = function () {
    const cue = this.visualFor(this.activeWorld)?.querySelector('[data-ld-continue-cue]') || this.root.querySelector('[data-ld-scroll-cue]');
    cue?.classList.add('is-intent-armed');
    window.clearTimeout(this.intentCueTimerV11);
    this.intentCueTimerV11 = window.setTimeout(() => cue?.classList.remove('is-intent-armed'), 700);
  };

  LDHomeFilmController.prototype.resetWheelIntentV11 = function () {
    this.wheelIntentV11 = { direction:null, accumulated:0, firstAt:0, lastAt:0, gestureCount:0, gestureAccum:0, gestureStartedAt:0, armedAt:0 };
  };

  LDHomeFilmController.prototype.navigateIntentV11 = function (direction, method) {
    this.resetWheelIntentV11();
    if (direction === 'next' && this.journeyState === JOURNEY_STATES.makeup) {
      this.journeyReleased = true;
      return this.jumpToStableWorld('epilogue');
    }
    if (direction === 'previous' && this.journeyState === JOURNEY_STATES.viso) {
      return this.returnToIntroReadyV11();
    }
    return this.requestJourneyStep(direction, method);
  };

  LDHomeFilmController.prototype.canScrollEditorialV11 = function (target, direction) {
    const panel = target instanceof Element ? target.closest('.ld-home-film__editorial-spread') : null;
    if (!panel || !this.root.contains(panel)) return false;
    if (!/^(auto|scroll)$/.test(window.getComputedStyle(panel).overflowY)) return false;
    const limit = panel.scrollHeight - panel.clientHeight;
    if (limit <= 1) return false;
    return direction === 'next' ? panel.scrollTop < limit - 1 : panel.scrollTop > 1;
  };

  LDHomeFilmController.prototype.onJourneyWheel = function (event) {
    if (!this.isJourneyInputActive()) return;
    const delta = Number(event.deltaY || 0);
    if (!delta) return;
    const direction = delta > 0 ? 'next' : 'previous';
    if (this.transitionInProgress || this.automaticPlayback) { if (event.cancelable) event.preventDefault(); return; }
    if (this.canScrollEditorialV11(event.target, direction)) {
      this.resetWheelIntentV11();
      return;
    }
    if (direction === 'previous' && this.journeyState === JOURNEY_STATES.introReady) return;
    if (event.cancelable) event.preventDefault();
    const amount = Math.abs(delta);
    if (amount < LD_V11.wheelMicro) return;
    const now = performance.now();
    if (!this.wheelIntentV11) this.resetWheelIntentV11();
    let intent = this.wheelIntentV11;
    if (!intent.lastAt || now - intent.lastAt > LD_V11.wheelIdleMs || (intent.direction && intent.direction !== direction)) {
      this.resetWheelIntentV11(); intent = this.wheelIntentV11;
    }
    const newGesture = !intent.lastAt || now - intent.lastAt > 120;
    if (!intent.direction) { intent.direction = direction; intent.firstAt = now; }
    if (newGesture) { intent.gestureAccum = 0; intent.gestureStartedAt = now; }
    intent.accumulated += amount;
    intent.gestureAccum += amount;
    intent.lastAt = now;
    if (now - intent.firstAt <= LD_V11.wheelDecisiveWindow && intent.accumulated >= LD_V11.wheelDecisive) {
      this.navigateIntentV11(direction,'wheel-decisive'); return;
    }
    if (intent.gestureAccum >= LD_V11.wheelGestureMin) {
      if (intent.gestureCount === 0) {
        intent.gestureCount = 1; intent.armedAt = now; this.armContinueCueV11();
      } else if (newGesture && now - intent.armedAt <= LD_V11.wheelDoubleWindow) {
        this.navigateIntentV11(direction,'wheel-double'); return;
      }
    }
  };

  LDHomeFilmController.prototype.onJourneyTouchStart = function (event) {
    if (!this.isJourneyInputActive() || event.touches?.length !== 1) {
      this.touchGestureV11 = null; return;
    }
    const now = performance.now();
    if (this.touchIntentV11 && now - this.touchIntentV11.lastAt > LD_V11.touchIdleMs) this.touchIntentV11 = null;
    this.touchGestureV11 = { startY:event.touches[0].clientY, lastY:event.touches[0].clientY, direction:null, amount:0, handled:false };
  };

  LDHomeFilmController.prototype.onJourneyTouchMove = function (event) {
    if (!this.isJourneyInputActive()) return;
    if (this.transitionInProgress || this.automaticPlayback) { if (event.cancelable) event.preventDefault(); return; }
    const gesture = this.touchGestureV11;
    if (!gesture || gesture.handled || event.touches?.length !== 1) return;
    if (gesture.nativeScroll) return;
    const delta = gesture.startY - event.touches[0].clientY;
    const direction = delta > 0 ? 'next' : 'previous';
    if (delta && this.canScrollEditorialV11(event.target, direction)) {
      gesture.nativeScroll = true;
      this.touchIntentV11 = null;
      return;
    }
    const amount = Math.abs(delta);
    if (amount < LD_V11.touchMin) return;
    if (event.cancelable) event.preventDefault();
    const now = performance.now();
    gesture.direction = direction; gesture.amount = amount;
    if (amount >= LD_V11.touchDecisive) {
      gesture.handled = true; this.touchIntentV11 = null; this.navigateIntentV11(direction,'touch-decisive'); return;
    }
    const armed = this.touchIntentV11;
    if (armed && armed.direction === direction && now - armed.lastAt <= LD_V11.touchIdleMs) {
      gesture.handled = true; this.touchIntentV11 = null; this.navigateIntentV11(direction,'touch-double'); return;
    }
    this.armContinueCueV11();
  };

  LDHomeFilmController.prototype.onJourneyTouchEnd = function () {
    const gesture = this.touchGestureV11;
    if (gesture && !gesture.handled && gesture.amount >= LD_V11.touchMin && gesture.amount < LD_V11.touchDecisive) {
      this.touchIntentV11 = { direction:gesture.direction, lastAt:performance.now() };
    }
    this.touchGestureV11 = null;
  };

  LDHomeFilmController.prototype.onScroll = function () {
    if (this.staticTier || this.journeyReleased || this.journeyInternalScroll || !this.isJourneyInputActive()) return v11Legacy.onScroll.call(this);
    if (this.geometryDirty) this.refreshGeometry();
    if (!this.geometry) return;
    if (Math.abs(window.scrollY - this.journeyAnchorY) > 3) this.scrollToJourneyAnchor();
    this.previousScrollY = window.scrollY;
  };

  LDHomeFilmController.prototype.returnToIntroReadyV11 = function () {
    this.cancelAutomaticPlayback('home_index_intro',{silent:true});
    this.introConsumed = true;
    this.completeIntroTitle('home-index');
    this.cancelIntro(); this.pauseIntroSequence();
    if (this.geometryDirty) this.refreshGeometry();
    const target = this.geometry?.intro?.start ?? 0;
    this.journeyReleased = false;
    this.journeyAnchorY = target;
    this.setJourneyState(JOURNEY_STATES.introReady);
    this.setAllVisualCategoriesInactive();
    this.setEpilogueActive(false);
    this.setActive('handoff', INTRO_STATES.hold, 0, '');
    this.targetVideoTime = this.config.intro.holdTime;
    this.seekVideo(this.targetVideoTime,{force:true});
    this.scrollToJourneyAnchor(target);
    this.revealIntroControls();
    this.setIntroControls(true);
    this.setHomeIndexTriggerVisibleV11(true);
    return true;
  };

  LDHomeFilmController.prototype.onRootClick = function (event) {
    const target = event.target instanceof Element ? event.target : null;
    const continueCue = target?.closest('[data-ld-continue-cue]');
    if (continueCue && this.root.contains(continueCue)) {
      event.preventDefault();
      this.navigateIntentV11('next','continue-cue');
      return;
    }
    return v11Legacy.onRootClick.call(this,event);
  };


  LDHomeFilmController.prototype.jumpToStableWorld = function (world) {
    const result = v11Legacy.jumpToStableWorld.call(this, world);
    if (!result) return result;
    if (CATEGORY_IDS.includes(world)) {
      const category = this.config.categories[CATEGORY_IDS.indexOf(world)];
      if (this.video && category) {
        this.video.pause();
        this.targetVideoTime = Math.max(category.videoStart, category.videoEnd - FRAME_DURATION);
        this.seekVideo(this.targetVideoTime, { force: true });
      }
      this.pauseInactivePortalVideosV11(world);
      this.activatePortalHandoffV11(world, null);
      const visual = this.visualFor(world);
      visual?.classList.remove('is-portal-under-phrases');
      visual?.classList.add('is-editorial-entering');
      const continueCue = visual?.querySelector('[data-ld-continue-cue]');
      const nextIndex = CATEGORY_IDS.indexOf(world) + 1;
      const nearWorld = nextIndex < CATEGORY_IDS.length ? CATEGORY_IDS[nextIndex] : '';
      if (continueCue) {
        const label = continueCue.querySelector('[data-ld-continue-label]');
        if (label) label.textContent = nearWorld ? `SCOPRI ${this.titleFor(nearWorld)}` : 'VAI AL FINALE';
        continueCue.setAttribute('aria-label', nearWorld ? `Scopri il capitolo ${this.titleFor(nearWorld)}` : 'Vai al finale');
      }
      this.later(() => this.setupQualityMarqueeV11(visual), 2000);
      this.later(() => visual?.classList.remove('is-editorial-entering'), 2200);
      this.later(() => this.setHomeIndexTriggerVisibleV11(true), 1750);
      this.syncPortalLifecycleV11(world, nearWorld);
    } else if (world === 'epilogue') {
      this.journeyReleased = true;
      this.syncPortalLifecycleV11('', '');
    }
    return result;
  };

  LDHomeFilmController.prototype.setHomeIndexTriggerVisibleV11 = function (visible) {
    const trigger = this.root.querySelector('[data-ld-home-index-trigger]');
    if (!trigger || trigger.hidden === !visible) return;
    trigger.hidden = !visible;
    if (this.homeIndexGeometryFrameV11) return;
    this.homeIndexGeometryFrameV11 = requestAnimationFrame(() => {
      this.homeIndexGeometryFrameV11 = 0;
      if (!this.destroyed) this.syncHomeStageGeometryV11();
    });
  };

  LDHomeFilmController.prototype.initHomeIndexV11 = function () {
    if (this.homeIndexV11Ready) return;
    const dialog = this.root.querySelector('[data-ld-home-index]');
    const trigger = this.root.querySelector('[data-ld-home-index-trigger]');
    if (!(dialog instanceof HTMLDialogElement) || !(trigger instanceof HTMLElement)) return;
    this.homeIndexV11Ready = true;
    this.homeIndexDialogV11 = dialog;
    this.homeIndexTriggerV11 = trigger;
    const close = (restoreFocus = true) => this.closeHomeIndexV11(restoreFocus);
    trigger.addEventListener('click', () => this.openHomeIndexV11('trigger'), { signal:this.signal });
    dialog.querySelector('[data-ld-home-index-close]')?.addEventListener('click', () => close(true), { signal:this.signal });
    dialog.querySelector('[data-ld-home-index-backdrop]')?.addEventListener('click', (event) => { event.preventDefault(); close(true); }, { signal:this.signal });
    dialog.addEventListener('cancel',(event)=>{ event.preventDefault(); close(true); },{signal:this.signal});
    dialog.querySelectorAll('[data-ld-home-index-jump]').forEach((button)=>button.addEventListener('click',()=>{
      const world=button.dataset.ldHomeIndexJump;
      this.closeHomeIndexV11(false);
      requestAnimationFrame(()=>{
        if(world==='intro') this.returnToIntroReadyV11();
        else if(world==='epilogue') {
          this.appPreviewAutoOpened = true;
          this.journeyReleased = true;
          this.jumpToStableWorld('epilogue');
          document.dispatchEvent(new CustomEvent('ld:app-preview-open',{detail:{source:'home-index-finale',trigger:button}}));
        } else this.jumpToStableWorld(world);
      });
    },{signal:this.signal}));
  };

  LDHomeFilmController.prototype.openHomeIndexV11 = function (source = 'trigger') {
    const dialog=this.homeIndexDialogV11;
    if (!dialog || dialog.open || this.isSuspended || document.querySelector('dialog[open]')) return false;
    this.homeIndexLastFocusV11 = source==='trigger' ? this.homeIndexTriggerV11 : document.activeElement;
    try { dialog.showModal(); } catch (_error) { dialog.setAttribute('open',''); }
    this.homeIndexTriggerV11?.setAttribute('aria-expanded','true');
    document.dispatchEvent(new CustomEvent('ld:home-suspend',{detail:{source:'home-index'}}));
    const first=dialog.querySelector('[data-ld-home-index-jump]');
    requestAnimationFrame(()=>first?.focus({preventScroll:true}));
    return true;
  };

  LDHomeFilmController.prototype.closeHomeIndexV11 = function (restoreFocus = true) {
    const dialog=this.homeIndexDialogV11;
    if (!dialog?.open) return;
    dialog.close();
    this.homeIndexTriggerV11?.setAttribute('aria-expanded','false');
    document.dispatchEvent(new CustomEvent('ld:home-resume',{detail:{source:'home-index'}}));
    this.syncDialogSuspension();
    this.setHomeIndexTriggerVisibleV11(true);
    if (restoreFocus && this.homeIndexTriggerV11?.isConnected) this.homeIndexTriggerV11.focus({preventScroll:true});
  };

  LDHomeFilmController.prototype.revealIntroControls = function () {
    const result=v11Legacy.revealIntroControls.call(this);
    this.setHomeIndexTriggerVisibleV11(true);
    return result;
  };

  LDHomeFilmController.prototype.destroyHomeIndexV11 = function () {
    if (this.homeIndexGeometryFrameV11) cancelAnimationFrame(this.homeIndexGeometryFrameV11);
    this.homeIndexGeometryFrameV11 = 0;
    if(this.homeIndexDialogV11?.open) try{this.homeIndexDialogV11.close();}catch(_error){}
  };

  LDHomeFilmController.prototype.pauseAutomaticPlaybackForSurface = function (reason) {
    const playback = this.automaticPlayback;
    if (!playback?.contractV11) return v11Legacy.pauseAutomaticPlaybackForSurface.call(this, reason);
    if (playback.surfacePaused) return true;
    if (this.automaticFrame) cancelAnimationFrame(this.automaticFrame);
    this.automaticFrame = 0;
    playback.surfacePaused = true;
    playback.surfacePauseReason = reason;
    playback.surfacePreviousMediaMode = playback.mediaMode;
    playback.v11ElapsedBeforePause = playback.startedAt === null ? 0 : Math.max(0, performance.now() - playback.startedAt);
    this.video?.pause();
    this.portalVideoForV11(playback.targetWorld)?.pause();
    playback.mediaMode = 'surface-paused';
    this.root.dataset.automaticMedia = 'surface-paused';
    return true;
  };

  LDHomeFilmController.prototype.resumeAutomaticPlaybackFromSurface = function () {
    const playback = this.automaticPlayback;
    if (!playback?.contractV11) return v11Legacy.resumeAutomaticPlaybackFromSurface.call(this);
    if (!playback.surfacePaused || this.destroyed || this.isSuspended) return false;
    delete playback.surfacePaused;
    delete playback.surfacePauseReason;
    playback.mediaMode = playback.portalHandoff ? 'portal' : 'native';
    this.root.dataset.automaticMedia = playback.mediaMode;
    playback.startedAt = performance.now() - Math.max(0, playback.v11ElapsedBeforePause || 0);
    delete playback.v11ElapsedBeforePause;
    if (playback.portalHandoff) {
      const portal = this.portalVideoForV11(playback.targetWorld);
      if (portal) { portal.defaultPlaybackRate = 1; portal.playbackRate = 1; }
      const promise = portal?.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => { this.root.dataset.mediaWarning = 'WARNING_MEDIA_FALLBACK'; });
    } else {
      this.setChapterPlaybackRate();
      const promise = this.video?.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => this.useVideoFallback('chapter_resume_rejected'));
    }
    this.automaticFrame = requestAnimationFrame((timestamp) => this.runAutomaticPlayback(timestamp));
    return true;
  };

  LDHomeFilmController.prototype.suspend = function (reason) {
    CATEGORY_IDS.forEach((world) => this.portalVideoForV11(world)?.pause());
    return v11Legacy.suspend.call(this,reason);
  };

  LDHomeFilmController.prototype.resume = function (reason) {
    const result=v11Legacy.resume.call(this,reason);
    if(!this.isSuspended && CATEGORY_IDS.includes(this.activeWorld) && this.activeState==='chapter-stable') {
      const index = CATEGORY_IDS.indexOf(this.activeWorld);
      const nearWorld = index >= 0 && index < CATEGORY_IDS.length - 1 ? CATEGORY_IDS[index + 1] : '';
      this.syncPortalLifecycleV11(this.activeWorld, nearWorld);
      this.activatePortalHandoffV11(this.activeWorld,null);
    }
    return result;
  };

  const v3Legacy = Object.freeze({
    connect: LDHomeFilmController.prototype.connect,
    destroy: LDHomeFilmController.prototype.destroy,
    setActive: LDHomeFilmController.prototype.setActive,
    runAutomaticPlayback: LDHomeFilmController.prototype.runAutomaticPlayback,
    setHomeIndexTriggerVisible: LDHomeFilmController.prototype.setHomeIndexTriggerVisibleV11,
    suspend: LDHomeFilmController.prototype.suspend,
    resume: LDHomeFilmController.prototype.resume,
  });

  LDHomeFilmController.prototype.isHomeFilmNavigationActiveV3 = function () {
    if (this.isSuspended || this.homeIndexDialogV11?.open) return false;
    const playback = this.automaticPlayback;
    if (!playback) {
      return (this.activeWorld === 'handoff' && this.introControlsRevealed) ||
        (CATEGORY_IDS.includes(this.activeWorld) && this.activeState === 'chapter-stable');
    }
    if (!playback.contractV11 || !CATEGORY_IDS.includes(playback.targetWorld)) return false;
    const elapsed = playback.surfacePaused
      ? Math.max(0, playback.v11ElapsedBeforePause || 0)
      : Math.max(0, performance.now() - (playback.startedAt || performance.now()));
    const stage = this.stageForElapsedV11(playback, elapsed);
    return stage?.id === 'category-title' || stage?.id?.startsWith('phrase-') || stage?.id?.startsWith('morph-');
  };

  LDHomeFilmController.prototype.setHomeIndexTriggerVisibleV11 = function () {
    return v3Legacy.setHomeIndexTriggerVisible.call(this, this.isHomeFilmNavigationActiveV3());
  };

  LDHomeFilmController.prototype.syncHomeIndexTriggerV3 = function () {
    this.setHomeIndexTriggerVisibleV11();
  };

  LDHomeFilmController.prototype.homeVideoDebugEnabledV4 = function () {
    return new URLSearchParams(window.location.search).get('ld_video_debug') === '1';
  };

  LDHomeFilmController.prototype.homeVideoDebugPortalV4 = function () {
    const playbackWorld = this.automaticPlayback?.targetWorld;
    const world = CATEGORY_IDS.includes(playbackWorld)
      ? playbackWorld
      : (CATEGORY_IDS.includes(this.activeWorld) ? this.activeWorld : '');
    return world ? { world, video: this.portalVideoForV11(world) } : { world: '', video: null };
  };

  LDHomeFilmController.prototype.updateHomeVideoDebugV4 = function () {
    if (!this.homeVideoDebugV4?.rail?.isConnected) return;
    const debug = this.homeVideoDebugV4;
    const portal = this.homeVideoDebugPortalV4();
    const formatTime = (value) => Number.isFinite(value) ? value.toFixed(2) : '--.--';
    const playbackState = (video) => video
      ? (video.paused ? 'PAUSED' : 'PLAYING')
      : '—';
    const worldLabel = {
      handoff: 'INTRO',
      viso: 'VISO',
      capelli: 'CAPELLI',
      corpo: 'CORPO',
      makeup: 'MAKE-UP',
      epilogue: 'FINALE',
    }[this.activeWorld] || 'INTRO';
    const masterTime = Number.isFinite(debug.masterMediaTime) && !this.video?.paused
      ? debug.masterMediaTime
      : this.video?.currentTime;
    const portalTime = portal.video && Number.isFinite(debug.portalMediaTime) && !portal.video.paused
      ? debug.portalMediaTime
      : portal.video?.currentTime;
    const progressEnd = Number(this.config?.epilogue?.videoEnd) || Number(this.video?.duration) || 1;
    const progress = clamp((Number(masterTime) || 0) / progressEnd, 0, 1);

    debug.fields.masterTime.textContent = `${formatTime(masterTime)} / ${formatTime(this.video?.duration)} s`;
    debug.fields.world.textContent = worldLabel;
    debug.fields.phase.textContent = this.activeState || this.compatibilityPhase?.() || '—';
    debug.fields.masterState.textContent = playbackState(this.video);
    debug.fields.portalTime.textContent = portal.video
      ? `${formatTime(portalTime)} / ${formatTime(portal.video.duration)} s`
      : '—';
    debug.fields.portalState.textContent = playbackState(portal.video);
    debug.fields.warning.textContent = this.root.dataset.mediaWarning || '—';
    debug.progress.style.transform = `scaleX(${progress})`;
    debug.rail.dataset.portalWorld = portal.world;
  };

  LDHomeFilmController.prototype.scheduleHomeVideoDebugV4 = function () {
    const debug = this.homeVideoDebugV4;
    if (!debug || this.destroyed) return;
    this.updateHomeVideoDebugV4();
    const portal = this.homeVideoDebugPortalV4().video;
    const frameSource = !this.video?.paused ? this.video : (!portal?.paused ? portal : null);
    if (frameSource && typeof frameSource.requestVideoFrameCallback === 'function') {
      debug.frameSource = frameSource;
      debug.frameKind = 'video';
      debug.frameId = frameSource.requestVideoFrameCallback((_now, metadata) => {
        debug.frameId = 0;
        if (frameSource === this.video) {
          debug.masterMediaTime = metadata?.mediaTime;
          debug.masterPresentedFrames = metadata?.presentedFrames;
        } else {
          debug.portalMediaTime = metadata?.mediaTime;
          debug.portalPresentedFrames = metadata?.presentedFrames;
        }
        this.scheduleHomeVideoDebugV4();
      });
      return;
    }
    debug.frameSource = null;
    debug.frameKind = 'animation';
    debug.frameId = requestAnimationFrame(() => {
      debug.frameId = 0;
      this.scheduleHomeVideoDebugV4();
    });
  };

  LDHomeFilmController.prototype.refreshHomeVideoDebugV4 = function () {
    const debug = this.homeVideoDebugV4;
    if (!debug) return;
    this.updateHomeVideoDebugV4();
    const portal = this.homeVideoDebugPortalV4().video;
    const desiredSource = !this.video?.paused ? this.video : (!portal?.paused ? portal : null);
    if (debug.frameKind !== 'video' || debug.frameSource === desiredSource) return;
    if (debug.frameId && typeof debug.frameSource?.cancelVideoFrameCallback === 'function') {
      debug.frameSource.cancelVideoFrameCallback(debug.frameId);
    }
    debug.frameId = 0;
    debug.frameSource = null;
    this.scheduleHomeVideoDebugV4();
  };

  LDHomeFilmController.prototype.initHomeVideoDebugV4 = function () {
    if (!this.homeVideoDebugEnabledV4() || this.homeVideoDebugV4) return;
    const rail = document.createElement('aside');
    rail.className = 'ld-home-video-debug';
    rail.dataset.ldHomeVideoDebug = '';
    rail.setAttribute('aria-label', 'Diagnostica video Home');
    rail.innerHTML = `<div class="ld-home-video-debug__readouts">
      <span>MASTER <output data-ld-home-video-debug-field="masterTime">--.-- / --.-- s</output></span>
      <span>active world: <output data-ld-home-video-debug-field="world">INTRO</output></span>
      <span>state: <output data-ld-home-video-debug-field="phase">—</output></span>
      <span>MASTER <output data-ld-home-video-debug-field="masterState">PAUSED</output></span>
      <span>LOOP <output data-ld-home-video-debug-field="portalTime">—</output></span>
      <span>LOOP <output data-ld-home-video-debug-field="portalState">—</output></span>
      <span class="ld-home-video-debug__warning">warning: <output data-ld-home-video-debug-field="warning">—</output></span>
    </div><div class="ld-home-video-debug__timeline" aria-hidden="true"><b data-ld-home-video-debug-progress></b></div>`;
    const timeline = rail.querySelector('.ld-home-video-debug__timeline');
    const timelineEnd = Number(this.config?.epilogue?.videoEnd) || VIDEO_DURATION;
    const tickTimes = [
      ...this.config.categories.map((category) => category.videoStart),
      this.config.epilogue.videoStart,
      this.config.epilogue.videoEnd,
    ];
    tickTimes.forEach((time) => {
      const tick = document.createElement('i');
      tick.style.setProperty('--ld-home-debug-tick', `${clamp(time / timelineEnd, 0, 1) * 100}%`);
      timeline.appendChild(tick);
    });
    document.body.appendChild(rail);
    const fields = {};
    rail.querySelectorAll('[data-ld-home-video-debug-field]').forEach((field) => {
      fields[field.dataset.ldHomeVideoDebugField] = field;
    });
    this.homeVideoDebugV4 = {
      rail,
      fields,
      progress: rail.querySelector('[data-ld-home-video-debug-progress]'),
      frameId: 0,
      frameKind: '',
      frameSource: null,
      masterMediaTime: Number.NaN,
      portalMediaTime: Number.NaN,
    };
    try {
      this.scheduleHomeVideoDebugV4();
    } catch (_error) {
      this.updateHomeVideoDebugV4();
    }
  };

  LDHomeFilmController.prototype.destroyHomeVideoDebugV4 = function () {
    const debug = this.homeVideoDebugV4;
    if (!debug) return;
    if (debug.frameId) {
      if (debug.frameKind === 'video' && typeof debug.frameSource?.cancelVideoFrameCallback === 'function') {
        debug.frameSource.cancelVideoFrameCallback(debug.frameId);
      } else cancelAnimationFrame(debug.frameId);
    }
    debug.rail.remove();
    this.homeVideoDebugV4 = null;
  };

  LDHomeFilmController.prototype.initHeroStillMotionV3 = function (attempt = 0) {
    if (this.heroMotionV3 || this.destroyed || this.reducedMotionQuery.matches || this.staticTier) return;
    const image = this.root.querySelector('[data-ld-hero-still] img');
    if (!(image instanceof HTMLImageElement)) return;
    if (!window.gsap) {
      if (attempt < 90) this.later(() => this.initHeroStillMotionV3(attempt + 1), 50);
      return;
    }
    this.heroMotionV3 = window.gsap.to(image, {
      scale: 1.035,
      xPercent: -0.55,
      yPercent: -0.35,
      duration: 12,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      transformOrigin: '50% 45%',
      force3D: true,
    });
  };

  LDHomeFilmController.prototype.connect = function () {
    const result = v3Legacy.connect.call(this);
    this.syncHomeIndexTriggerV3();
    this.initHeroStillMotionV3();
    this.initHomeVideoDebugV4();
    if (document.querySelector('#Details-menu-drawer-container[open]')) this.suspend('menu');
    this.reducedMotionQuery.addEventListener?.('change', (event) => {
      if (event.matches) {
        this.heroMotionV3?.kill();
        this.heroMotionV3 = null;
        window.gsap?.set?.(this.root.querySelector('[data-ld-hero-still] img'), { clearProps: 'transform' });
      } else this.initHeroStillMotionV3();
    }, { signal: this.signal });
    return result;
  };

  LDHomeFilmController.prototype.setActive = function (world, state, localProgress, visualState) {
    const result = v3Legacy.setActive.call(this, world, state, localProgress, visualState);
    document.body?.classList.toggle('ld-home-past-hero', world !== 'handoff');
    this.syncHomeIndexTriggerV3();
    this.refreshHomeVideoDebugV4();
    if (world === 'handoff' && !this.isSuspended) this.heroMotionV3?.play();
    else this.heroMotionV3?.pause();
    return result;
  };

  LDHomeFilmController.prototype.runAutomaticPlayback = function (timestamp) {
    const result = v3Legacy.runAutomaticPlayback.call(this, timestamp);
    this.syncHomeIndexTriggerV3();
    return result;
  };

  LDHomeFilmController.prototype.suspend = function (reason) {
    this.heroMotionV3?.pause();
    const result = v3Legacy.suspend.call(this, reason);
    this.syncHomeIndexTriggerV3();
    this.refreshHomeVideoDebugV4();
    return result;
  };

  LDHomeFilmController.prototype.resume = function (reason) {
    const result = v3Legacy.resume.call(this, reason);
    if (!this.isSuspended && this.activeWorld === 'handoff') this.heroMotionV3?.play();
    this.syncHomeIndexTriggerV3();
    this.refreshHomeVideoDebugV4();
    return result;
  };

  LDHomeFilmController.prototype.destroy = function () {
    this.destroyHomeVideoDebugV4();
    this.heroMotionV3?.kill();
    this.heroMotionV3 = null;
    document.body?.classList.remove('ld-home-past-hero');
    return v3Legacy.destroy.call(this);
  };

  const rootsIn = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-ld-home-film][data-dv2-home]')) roots.push(scope);
    scope.querySelectorAll?.('[data-ld-home-film][data-dv2-home]').forEach((root) => roots.push(root));
    return roots;
  };

  const init = (scope = document) => {
    rootsIn(scope).forEach((root) => {
      if (INSTANCES.has(root)) return;
      const existingPrimary = Array.from(CONNECTED).find((controller) => !controller.destroyed && !controller.designMode && !controller.forcedStatic);
      const controller = new LDHomeFilmController(root, {
        forcedStatic: Boolean(existingPrimary && !window.Shopify?.designMode),
      });
      INSTANCES.set(root, controller);
      controller.connect();
    });
  };

  const unload = (scope) => {
    rootsIn(scope).forEach((root) => {
      INSTANCES.get(root)?.destroy();
      INSTANCES.delete(root);
    });
  };

  const getInstance = () => {
    const root = document.querySelector('[data-ld-home-film][data-dv2-home]');
    if (root) {
      const instance = INSTANCES.get(root) || root.ldHomeFilmController;
      if (instance && !instance.destroyed) return instance;
    }
    return Array.from(CONNECTED).find((controller) => !controller.destroyed) || null;
  };

  window.DV2Home = Object.freeze({
    getInstance,
    getState() {
      return getInstance()?.getState() || null;
    },
    restore(record) {
      return getInstance()?.restore(record) || false;
    },
    refresh() {
      getInstance()?.refresh();
    },
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  document.addEventListener('shopify:section:reorder', () => getInstance()?.refresh());
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches?.('[data-ld-home-film][data-dv2-home]')
      ? event.target
      : event.target.querySelector?.('[data-ld-home-film][data-dv2-home]');
    INSTANCES.get(root)?.selectForEditor();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const root = event.target.matches?.('[data-ld-home-film][data-dv2-home]')
      ? event.target
      : event.target.querySelector?.('[data-ld-home-film][data-dv2-home]');
    INSTANCES.get(root)?.deselectForEditor();
  });
})();
